import { dashboardHomeSurface } from "./lib/dashboardHomeSurface.js";
import { phAttrs } from "./lib/analytics/attrs.js";
import { ACTION, ATTRIBUTE } from "./lib/analytics/surfaceVocabulary.js";
import { Fragment, startTransition, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import DateRangePicker from "./components/DateRangePicker.jsx";
import GenerationPicker from "./components/GenerationPicker.jsx";
import OriginMark from "./components/OriginMark.jsx";
import HoverTooltip from "./components/HoverTooltip.jsx";
import SecurityShieldIcon from "./components/SecurityShieldIcon.jsx";
import SidebarFooter from "./components/SidebarFooter.jsx";
import ScopeSidebar from "./components/ScopeSidebar.jsx";
import { CACHE_NOTIFY } from "./components/SidebarNavRuntime.jsx";
import { readUrlParams, writeUrlParam, applyScopeNavParams } from "./lib/urlParams.js";
import { enterDetailFullScreen } from "./lib/enterDetailFullScreen.js";
import { exitDetailFullScreen } from "./lib/exitDetailFullScreen.js";
import { useUrlParamMerger } from "./lib/useUrlParamMerger.js";
import { useDebouncedValue } from "./lib/useDebouncedValue.js";
import { pipelineFilterText, andQueriesAll } from "./lib/queryPipeline.js";
import { suggestValues as suggestQueryValues } from "./lib/queryLangSuggest.js";
import { encodeUrlState, decodeUrlState } from "./lib/urlCodec.js";
import { pruneCacheForUrl, liveScopePathsFor } from "./lib/sidebarCacheUrl.js";
import { GEN_PARAM, notifyGenerationPinChange } from "./lib/generationPin.js";
import { resetSharedDetailPanelWidth } from "./components/SplitPage.jsx";
import ScopeColumns from "./components/ScopeColumns.jsx";
import ScrollArea from "./components/ScrollArea.jsx";
import TabStrip from "./components/TabStrip.jsx";
import {
  clampSidebarWidth,
  COLLAPSED_SIDEBAR_WIDTH,
  DEFAULT_SIDEBAR_WIDTH,
  initialSidebarCollapsed,
  initialTheme,
  persistShellState,
  SIDEBAR_STORAGE_KEY,
  readStoredNumber,
} from "./lib/shell.js";
import { decodeScopePath, encodeScopePath, scopeStepLabel, useLabels, DIMENSIONS } from "./lib/dimensionDefs.js";
import { useScopeHydration, useWorkerReady } from "./lib/dataClient.js";
import ScopedPage from "./pages/ScopedPage.jsx";
import AdminPage from "./pages/AdminPage.jsx";
import AdminMenu, { ADMIN_VIEWS, MODES, SHOWN_VIEW_MODES, modeForView, firstVisibleViewForMode } from "./components/AdminMenu.jsx";
import StudioMenu from "./components/StudioMenu.jsx";
import StudioOverview from "./components/StudioOverview.jsx";
import TenantViewCloudNotice from "./components/TenantViewCloudNotice.jsx";
import TenantViewStartupNotice from "./components/TenantViewStartupNotice.jsx";
import {
  ComplianceIntegrationHealthBanner,
  ComplianceIntegrationHealthProvider,
} from "./components/ComplianceIntegrationHealth.jsx";
import CanvasMenu from "./components/CanvasMenu.jsx";
import CanvasHomePage from "./pages/CanvasHomePage.jsx";
import WorkspaceHome from "./pages/WorkspaceHome.jsx";
import WorkspaceThreads, { requestThreadsSearch } from "./components/WorkspaceThreads.jsx";
import { startWorkspaceInDeck, useDeckBlankInFront } from "./lib/deckRequests.js";
import TraceCanvasOverlay from "./components/TraceCanvasOverlay.jsx";
import { useTraceCanvasOpen } from "./lib/traceCanvasStore.js";
import ConnectPage from "./pages/ConnectPage.jsx";
import { subscribeDock, getDockChatBar } from "./lib/dockStore.js";
import { orderCanvases, renameHomeTab, removeHomeTab } from "./lib/dashboardHome.js";
import { AUTH_REQUIRED, defaultLanding, comparisonsEnabled, securityTabEnabled } from "./lib/featureFlags.js";
import { coerceComparisonLens } from "./lib/comparisonLens.js";
import { listKernels, subscribeKernels, createBlankKernelCloudFirst } from "./lib/kernelStore.js";
import { loadWorkflows, subscribeWorkflows, createBlankWorkflow } from "./lib/workflowStore.js";
import { listCanvasTemplates, subscribeCanvasTemplates, createBlankCanvasTemplateCloudFirst } from "./lib/canvasTemplateStore.js";
import { subscribeNavigation } from "./lib/navigationStore.js";
import { applyTraceNavParams } from "./lib/navIntent.js";
import { FINDING_FOCUS_PARAM } from "./lib/traceReviewFindings.js";
import { listTriggers, subscribeTriggers } from "./lib/triggers/triggerStore.js";
import { getCurrentUser } from "./lib/userInfo.js";
import { initializeTenantViewStudio } from "./lib/tenantViewStartup.js";
import {
  getTenantViewCloudState,
  subscribeTenantViewCloud,
} from "./lib/tenantViewCloudStore.js";
import {
  areStudioInventoriesReady,
  nextStudioTabState,
} from "./lib/studioTabReaping.js";
import { getPins, getPin, isPinned, updatePin, subscribePins, pinToken, parsePinToken } from "./lib/overviewHome.js";
import {
  encodeTab,
  modeForTab,
  scopePathFor,
  tabsEqual,
  findTabIndex,
  renameTab,
  tabLabel,
  tabTitle,
  makeScopeTab,
  makeInfluenceTab,
  parseInfluenceTab,
  makeStudioTab,
  makeKernelTab,
  makeCanvasTab,
  makeWorkflowTab,
  makeTriggerTab,
  makeSkillsTab,
} from "./lib/tabModel.js";
import { readExplorerStrip, readStudioStrip, requestedViewFor, resolveMode, shownModes } from "./lib/routeModel.js";
import { expandHierarchicalScope } from "./lib/scopeHierarchy.js";
import ChatConsole from "./components/ChatConsole.jsx";
import { useHasPermission, useMyPermissions } from "./lib/permissions.js";
import { useTenantEntitlements } from "./lib/entitlements.js";
import {
  isConnectFrontDoorPinned,
  pinConnectFrontDoor,
  shouldEngageConnectGate,
  unpinConnectFrontDoor,
  useConnectState,
} from "./lib/connectState.js";
import { PERMISSIONS } from "./lib/permissionKeys.js";
import { getAuthIdentityEpoch, onAuthIdentityChange } from "./lib/authTokenStore.js";
import { isSigningOut } from "./lib/authBridge.js";
import AuthIdentityGate from "./components/AuthIdentityGate.jsx";
import TenantLinkGate from "./components/TenantLinkGate.jsx";
import UnavailableLinkPage from "./components/UnavailableLinkPage.jsx";
import StartupScreen from "./components/StartupScreen.jsx";
import { isUnavailableCanvasLink, isUnavailableExplorerLink, isUnavailableStudioLink, openCurrentOrganizationHome, subscribeItemLinkRecovery } from "./lib/tenantLink.js";

const ADMIN_VIEW_IDS = new Set(ADMIN_VIEWS.map((v) => v.id));

// ── Icons ─────────────────────────────────────────────────────────

function SidebarToggleIcon({ collapsed }) {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      {collapsed ? <path d="M6 3.5 10.5 8 6 12.5" /> : <path d="M10 3.5 5.5 8 10 12.5" />}
    </svg>
  );
}

// Per-mode rail icons. Same 16-grid + 1.4 stroke + round-caps family
// as the other rail/sidebar icons. Each icon is rendered as a
// currentColor stroke so the rail's active/idle color treatment from
// [data-origin-mode] applies uniformly.
function WorkspaceIcon() {
  // A board: two cards laid on one clock.
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2.5" width="12" height="11" rx="1.5" />
      <path d="M4.5 6h5M6.5 9.5h5" />
    </svg>
  );
}

function ExploreIcon() {
  // Compass — "explore / navigate the data". Reads as a mode, not the
  // search-tool look of a magnifier (which clashed with the geometric rail
  // icons). Circle + a NE-pointing needle.
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="8" r="6" />
      <path d="m10.6 5.4-1.5 3.7-3.7 1.5 1.5-3.7z" />
    </svg>
  );
}

function StudioIcon() {
  // 2×2 grid of small panels — reads as "a collection of arranged
  // items" which is what a canvas is (a grid of kernel widgets) and
  // what Studio itself manages (a library of kernels and canvases).
  // Integer coordinates with 1-unit gaps so cells stay crisp at 14px.
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="4" height="4" />
      <rect x="9" y="3" width="4" height="4" />
      <rect x="3" y="9" width="4" height="4" />
      <rect x="9" y="9" width="4" height="4" />
    </svg>
  );
}

function SettingsIcon() {
  // Two horizontal sliders with offset handles. Reads cleaner at
  // 14px than a gear (which loses its teeth) while still saying
  // "configuration / preferences."
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2.5 5h11" />
      <circle cx="6" cy="5" r="1.5" />
      <path d="M2.5 11h11" />
      <circle cx="10" cy="11" r="1.5" />
    </svg>
  );
}

function EndpointsIcon() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2.5" y="3" width="11" height="8" rx="1.2" />
      <path d="M6 13h4M8 11v2" />
      <circle cx="11.2" cy="5.4" r="0.8" fill="currentColor" stroke="none" />
    </svg>
  );
}

function GovernanceIcon() {
  // Shield + policy check — security administration without reusing the
  // general settings sliders. The open check reads at the rail's 14px size.
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 2.2 13 4v3.7c0 3.1-2 5.1-5 6.2-3-1.1-5-3.1-5-6.2V4z" />
      <path d="m5.7 7.9 1.5 1.5 3.2-3.2" />
    </svg>
  );
}

function CanvasesIcon() {
  // A framed dashboard panel — header bar + small bars inside. Reads as "a
  // composed view of charts," distinct from Studio's 2×2 grid.
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2.5" y="3" width="11" height="10" rx="1.5" />
      <path d="M2.5 6h11" />
      <path d="M5 10.5v-1.5M8 10.5v-3M11 10.5v-2" />
    </svg>
  );
}

const MODE_ICONS = {
  workspace: WorkspaceIcon,
  dashboard: CanvasesIcon,
  explore: ExploreIcon,
  security: SecurityShieldIcon,
  studio: StudioIcon,
  endpoints: EndpointsIcon,
  governance: GovernanceIcon,
  admin: SettingsIcon,
};

// Per-step row icon button. Square, no fill, hover bumps to ink so
// the affordance reads as "interactive icon" without competing with
// the row's primary label/value text.
function ScopeStepIconButton({ children, label, title, disabled = false, disabledTitle, onClick, onMouseEnter }) {
  const effectiveTitle = disabled && disabledTitle ? disabledTitle : title;
  return (
    <button
      type="button"
      role="menuitem"
      aria-label={label}
      title={effectiveTitle}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      onMouseEnter={onMouseEnter}
      className={[
        "inline-flex h-[26px] w-[26px] items-center justify-center transition-colors [&_svg]:block [&_svg]:h-[14px] [&_svg]:w-[14px]",
        disabled
          ? "cursor-not-allowed text-[color:var(--muted-soft)] opacity-40"
          : "cursor-pointer text-[color:var(--muted)] hover:text-[color:var(--ink)]",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

// Per-step popover anchored to the active tab's ▾ trigger (or the
// title button itself when the user clicks the active tab to toggle
// the menu). For each step in the active path, exposes three icon
// actions:
//   Zoom (+)     — trim the active tab's scope to end at this step
//   Open-in-new  — same trim, but in a fresh tab
//   ×            — remove just this filter, keep the rest
function ScopeStepMenu({ scopePath, anchor, onClose, onJump, onOpenInNew, onDrop }) {
  const ref = useRef(null);
  // Left edge aligns to the tab's left edge so the menu visually
  // drops out of the tab itself. The tab's width is only a MINIMUM:
  // the menu grows to fit its content (capped by the viewport), so a
  // narrow tab or scaled-up browser text doesn't crush the rows into
  // single-character ellipses.
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  // Which row the cursor is over. Used to dim the rows BELOW it
  // so the operator can see at a glance which deeper filters get
  // dropped if they click. Drop / open-in-new icons stop propagation
  // and don't set this so their hover doesn't imply trimming.
  const [hoveredIdx, setHoveredIdx] = useState(null);
  // Rendered menu width, measured after layout. Only used to decide
  // whether the menu outgrew the tab and needs the top-border
  // extension segment past the tab's right edge.
  const [menuWidth, setMenuWidth] = useState(0);

  useLayoutEffect(() => {
    if (!anchor) return undefined;
    const place = () => {
      const r = anchor.getBoundingClientRect();
      setPos({ top: r.bottom, left: r.left, width: r.width });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [anchor]);

  useLayoutEffect(() => {
    setMenuWidth(ref.current ? ref.current.offsetWidth : 0);
  }, [pos, scopePath]);

  useEffect(() => {
    const onDown = (e) => {
      if (ref.current?.contains(e.target)) return;
      if (anchor?.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [anchor, onClose]);

  // FocusTabStrip never opens the menu on an empty path (the chevron
  // is hidden in that case), so we don't render an empty-state body
  // here.
  if (!scopePath.length) return null;

  return (
    <div
      ref={ref}
      role="menu"
      // Left is pinned to the active tab's bounding rect and the tab's
      // width is the menu's min-width, so the menu drops cleanly out of
      // the tab itself. Width is otherwise content-driven (max-content),
      // capped at 340px / the viewport's right edge. border-t-0 +
      // top: r.bottom seam-merge the menu's top edge with the tab's
      // open bottom; when the menu outgrows the tab, the span below
      // draws the missing top-border segment past the tab's right edge.
      className="fixed z-[9999] grid gap-[2px] border border-[color:var(--line-strong)] border-t-0 bg-[color:var(--bg)] p-[2px] shadow-lg"
      style={{
        top: `${pos.top}px`,
        left: `${pos.left}px`,
        minWidth: `${pos.width}px`,
        width: "max-content",
        maxWidth: `min(340px, calc(100vw - ${pos.left + 4}px))`,
      }}
    >
      {menuWidth > pos.width + 1 ? (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute right-0 top-0 h-[1px] bg-[color:var(--line-strong)]"
          style={{ left: `${Math.max(0, pos.width - 2)}px` }}
        />
      ) : null}
      {scopePath.map((step, i) => {
        const dim = DIMENSIONS[step.dim];
        const value = scopeStepLabel(step);
        const dimLabel = dim?.label || step.dim;
        const isLast = i === scopePath.length - 1;
        const rowClickable = !isLast;
        // Dim rows that would be trimmed if the user clicked the
        // currently-hovered row. Reads as "these are about to go".
        const willBeTrimmed = hoveredIdx !== null && i > hoveredIdx;
        return (
          <div
            key={`${step.dim}:${i}`}
            role={rowClickable ? "menuitem" : undefined}
            tabIndex={rowClickable ? 0 : undefined}
            onMouseEnter={rowClickable ? () => setHoveredIdx(i) : undefined}
            onMouseLeave={rowClickable ? () => setHoveredIdx((cur) => cur === i ? null : cur) : undefined}
            onClick={rowClickable ? () => { onClose(); onJump(i); } : undefined}
            onKeyDown={rowClickable ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClose();
                onJump(i);
              }
            } : undefined}
            title={rowClickable ? "Jump to this scope and remove deeper filters" : undefined}
            className={[
              "grid items-center gap-x-[6px] gap-y-[2px] px-[8px] py-[6px] [grid-template-columns:minmax(0,1fr)_auto] transition-opacity",
              rowClickable
                ? "cursor-pointer hover:bg-[color:var(--control-bg)]"
                : "cursor-default",
              willBeTrimmed ? "opacity-30" : "",
            ].join(" ")}
          >
            <div className="grid min-w-0 gap-[1px]">
              {/* truncate matters beyond long names: without it, a label that
                  outgrows the cell (e.g. under a browser minimum font size)
                  widens the whole text column and slides both lines under the
                  icon buttons. */}
              <span className="truncate text-[10px] leading-[1.1] tracking-[0.06em] uppercase text-[color:var(--muted-soft)]">
                {dimLabel}
              </span>
              <span className="truncate text-[12px] leading-[1.2] text-[color:var(--ink)]">
                {value}
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-[1px]">
              <ScopeStepIconButton
                label="Open in new tab"
                title="Open this scope (truncated here) in a new tab"
                // Same trim semantics as Jump (the new tab just opens
                // a fresh tab at this trimmed scope), so propagating
                // hover up to the row is fine — deeper rows still get
                // dimmed.
                onClick={(e) => { e.stopPropagation(); onClose(); onOpenInNew(i); }}
              >
                {/* Open-in-new — box (lower-left) + arrow exiting top-right */}
                <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 5v6h6V8" />
                  <path d="M7 7l4-4" />
                  <path d="M8 3h3v3" />
                </svg>
              </ScopeStepIconButton>
              <ScopeStepIconButton
                label="Drop this filter"
                title="Remove just this filter, keep the rest"
                // Drop only removes THIS row; deeper rows survive. Stop
                // mouseenter from bubbling to the row so hovering this
                // icon doesn't dim the rows below — that would imply
                // they're about to be removed when they aren't.
                onMouseEnter={(e) => { e.stopPropagation(); setHoveredIdx(null); }}
                onClick={(e) => { e.stopPropagation(); onClose(); onDrop(i); }}
              >
                {/* × matches the tab close glyph */}
                <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4l6 6M10 4l-6 6" />
                </svg>
              </ScopeStepIconButton>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Unified tab strip ────────────────────────────────────────────
//
// "+" affordance that opens a small menu instead of a single action —
// used by the Library strip to create a kernel, canvas, or workflow.
// (The Explore strip keeps the single-action `onOpen` "+".)
function NewTabMenu({ items }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    // Position the menu in a portal (document.body) so it escapes the
    // tab strip's overflow clipping. Re-place on scroll/resize.
    const place = () => {
      const r = btnRef.current?.getBoundingClientRect();
      if (r) setPos({ top: r.bottom + 2, right: Math.max(4, window.innerWidth - r.right) });
    };
    place();
    const onDown = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);
  return (
    <div className="relative flex min-h-[38px] shrink-0 items-stretch border border-b-0 border-[color:var(--line)] bg-[color:var(--control-bg)]">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="New…"
        aria-label="New library item"
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex w-[28px] shrink-0 items-center justify-center px-[8px] text-[14px] leading-none text-[color:var(--muted-soft)] hover:text-[color:var(--muted)] transition-colors"
      >
        +
      </button>
      {open && pos
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              style={{ position: "fixed", top: pos.top, right: pos.right, zIndex: 1000 }}
              className="min-w-[150px] border border-[color:var(--line)] bg-[color:var(--bg)] py-[2px] shadow-[0_4px_16px_rgba(0,0,0,0.4)]"
            >
              {items.map((it) => (
                <button
                  key={it.label}
                  type="button"
                  role="menuitem"
                  onClick={() => { setOpen(false); it.onSelect(); }}
                  className="block w-full px-[10px] py-[6px] text-left text-[12px] text-[color:var(--muted)] hover:bg-[color:var(--control-bg)] hover:text-[color:var(--ink)] cursor-pointer"
                >
                  {it.label}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

// Each tab is a typed entry (scope / studio / kernel / canvas /
// settings) — see lib/tabModel.js. Clicking a tab switches the app's
// active surface to that tab's payload; mode is derived from the
// tab's kind. A "+" action button opens a new scope overview tab
// (the lightest-weight starting point). Closing a tab removes it
// from the list and shifts active to a neighbor. Only scope tabs
// expose the ▾ scope-step menu — other tab kinds have no per-step
// drill-down to surface.
function FocusTabStrip({ tabs: tabList, activeIdx, onActivate, onClose, onOpen, newTabMenu = null, onReorder, onJumpStep, onOpenStepInNew, onDropStep, actions: extraActions = null, dimmed = false }) {
  // Pre-warm scope labels across every open scope tab so switching
  // tabs doesn't briefly show an id-prefix label.
  const allDeepest = tabList
    .map((t) => {
      if (t.kind !== "scope") return null;
      const path = scopePathFor(t) || [];
      return path[path.length - 1];
    })
    .filter(Boolean);
  useLabels(allDeepest);

  // Anchor element for the per-step menu. null = closed; an
  // HTMLElement = open, with the dropdown positioned beneath it.
  const [menuAnchor, setMenuAnchor] = useState(null);

  const activeIsScope = tabList[activeIdx]?.kind === "scope";
  const activeScopePath = activeIsScope ? (scopePathFor(tabList[activeIdx]) || []) : [];

  const renderTabs = tabList.map((tab, i) => {
    const isActive = i === activeIdx;
    const label = tabLabel(tab, { adminViews: ADMIN_VIEWS });
    const title = tabTitle(tab, { adminViews: ADMIN_VIEWS });
    // ▾ menu only on the active scope tab with at least one filter.
    const isScopeWithSteps = tab.kind === "scope" && (scopePathFor(tab) || []).length > 0;
    return {
      id: `tab:${i}`,
      kind: tab.kind,
      label,
      active: isActive,
      dot: isActive ? "bg-[color:var(--green)]" : undefined,
      title,
      onSelect: () => onActivate(i),
      onClose: tabList.length > 1 ? () => onClose(i) : undefined,
      onMenu: isActive && isScopeWithSteps ? (anchor) => setMenuAnchor(anchor) : undefined,
      menuOpen: isActive && isScopeWithSteps && !!menuAnchor,
    };
  });

  // Wrap the + button in the same tab-shaped box the per-tab × sits
  // in: min-h-[38px], top + side border with no border-b so the box
  // touches the content baseline the same way an inactive tab does,
  // --control-bg fill. The button glyph inside reuses the × button's
  // styling.
  // "+" button is only meaningful when there's a parent "blank
  // surface" to open into (Explore's Overview scope tab). For strips
  // where every tab comes from clicking a sidebar item (Studio's
  // kernel/canvas tabs), `onOpen` is null and the + button is
  // omitted — opening a new tab is what the sidebar is for.
  const newTabAction = newTabMenu ? (
    <NewTabMenu items={newTabMenu} />
  ) : onOpen ? (
    <div className="relative flex min-h-[38px] shrink-0 items-stretch overflow-hidden border border-b-0 border-[color:var(--line)] bg-[color:var(--control-bg)]">
      <button
        type="button"
        onClick={onOpen}
        title="Open new tab"
        aria-label="Open new tab"
        className="inline-flex w-[28px] shrink-0 items-center justify-center px-[8px] text-[14px] leading-none text-[color:var(--muted-soft)] hover:text-[color:var(--muted)] transition-colors"
      >
        +
      </button>
    </div>
  ) : null;
  // SplitPage clones FocusTabStrip and slots in an "Expand panel"
  // chevron via the `actions` prop when the detail column is
  // collapsed. Render both: + first (when present), then any
  // caller-supplied actions to the right of it.
  const actions = (
    <>
      {newTabAction}
      {extraActions}
    </>
  );

  return (
    <>
      {/* `dimmed` fades the strip out in pin mode — the full-screen surface
          grows over it, but fading avoids its borders peeking at the edges
          during the FLIP. Height is preserved (no layout shift). */}
      <div
        style={{ opacity: dimmed ? 0 : 1, transition: "opacity 200ms ease", pointerEvents: dimmed ? "none" : undefined }}
        aria-hidden={dimmed || undefined}
      >
      <TabStrip
        tabs={renderTabs}
        className="pt-[11px] max-[900px]:pt-[8px]"
        actions={actions}
        onReorder={tabList.length > 1 ? onReorder : undefined}
      />
      </div>
      {menuAnchor ? (
        <ScopeStepMenu
          scopePath={activeScopePath}
          anchor={menuAnchor}
          onClose={() => setMenuAnchor(null)}
          onJump={(stepIdx) => onJumpStep(stepIdx)}
          onOpenInNew={(stepIdx) => onOpenStepInNew(stepIdx)}
          onDrop={(stepIdx) => onDropStep(stepIdx)}
        />
      ) : null}
    </>
  );
}

// Studio's focus header: the tab strip plus the tenant-view notices that sit
// beside it. This has to be a component rather than a Fragment — SplitPage
// clones the `focusHeader` element to inject an "Expand panel" chevron through
// `actions`, and a Fragment accepts only `key`/`children`, so the chevron is
// discarded. Declaring it at module scope keeps the element type stable across
// App renders; a component defined in the render body would get a fresh
// identity each time and remount the strip, dropping its state.
function StudioFocusHeader({
  actions = null,
  strip,
  legacyImportReport,
  onApproveLegacySecrets,
  onDismissLegacyReport,
}) {
  return (
    <>
      <FocusTabStrip {...strip} actions={actions} />
      <TenantViewCloudNotice />
      <TenantViewStartupNotice
        report={legacyImportReport}
        onApproveSecrets={onApproveLegacySecrets}
        onDismiss={onDismissLegacyReport}
      />
    </>
  );
}

// ── App ───────────────────────────────────────────────────────────

function AppContent({ authContextEpoch }) {
  const [startupError, setStartupError] = useState(null);
  const workerReady = useWorkerReady(setStartupError);
  const [searchParams, setSearchParams] = useSearchParams();
  const shellRef = useRef(null);
  const sidebarRef = useRef(null);

  // ── URL state ──
  const range = searchParams.get("range") || "1M";

  // Per-mode tab strips. Each top-level mode keeps its own independent
  // tab list or selected admin view, so switching modes does not disturb
  // index so switching modes doesn't disturb the others.
  //
  // URL state:
  //   ?mode=dashboard|explore|studio|endpoints|governance|admin — active rail mode
  //                            (default: dashboard, cleared from the URL)
  //   ?tabs=…&active=N       — explore scope tabs (legacy shape kept
  //                            so old scope deep-links still resolve)
  //   ?studioTabs=…&studioActive=N  — studio tabs (typed: studio:,
  //                            kernel:cmd, canvas:slug)
  //   ?adminView=<id>        — single active Endpoints/Admin view
  //   ?settingsView=<id>     — legacy alias, read and then replaced
  // defaultLanding() is the runtime kill switch (VITE_DEFAULT_LANDING /
  // localStorage) for the explore→dashboard front door. When reverted to
  // "explore" the Dashboard mode is fully off: dropped from VALID_MODES (so a
  // stray ?mode=dashboard can't reach it) and hidden from the rail below.
  const dashboardEnabled = defaultLanding() === "dashboard";
  // securityTabEnabled() gates the Security rail entry. Its views are already
  // gated out of ADMIN_VIEWS (see AdminMenu), which hides the rail button;
  // dropping the mode here too keeps a stray ?mode=security from landing on
  // an empty Security shell.
  const securityEnabled = securityTabEnabled();
  const VALID_MODES = shownModes({ dashboardEnabled, securityEnabled });

  // Explore: scope tabs plus influence-trace tabs. The strip's grammar,
  // including the legacy unprefixed encoding that keeps old scope deep links
  // working, is routeModel.js's, shared with the analytics layer.
  const { explorerTabs, explorerActiveIdx, explorerActiveTab, scopePath, influenceTab } = useMemo(() => {
    const { tabs: parsed, activeIdx: idx } = readExplorerStrip(searchParams);
    return {
      explorerTabs: parsed,
      explorerActiveIdx: idx,
      explorerActiveTab: parsed[idx],
      scopePath: scopePathFor(parsed[idx]) || [],
      influenceTab: parseInfluenceTab(parsed[idx]),
    };
  }, [searchParams]);

  const influenceSeqRef = useRef(0);

  // Studio: typed tab strip (overview / kernel / canvas).
  const { studioTabs, studioActiveIdx, studioActiveTab, adminView: studioView, studioSelectedItem } = useMemo(() => {
    // Includes the legacy synthesis from the older single-item params; see
    // routeModel.js.
    const { tabs: parsed, activeIdx: idx } = readStudioStrip(searchParams);
    const active = parsed[idx];
    const view = active?.kind === "kernel" ? "kernels"
      : active?.kind === "canvas" ? "canvases"
      : active?.kind === "workflow" ? "workflows"
      : active?.kind === "trigger" ? "signals"
      : active?.kind === "skills" ? "skills"
      : null;
    const item = (active?.kind === "kernel" || active?.kind === "canvas" || active?.kind === "workflow" || active?.kind === "trigger") ? active.payload : null;
    return {
      studioTabs: parsed,
      studioActiveIdx: idx,
      studioActiveTab: active,
      adminView: view,
      studioSelectedItem: item,
    };
  }, [searchParams]);

  // RBAC: Explore (the analytics scope explorer) requires the base
  // analytics-read verb. A role without it (e.g. IT/Fleet Admin) must
  // never see or land on Explore. It lands in Endpoints or Admin according
  // to its permission set. `analytics.execute`
  // is implied server-side by the aggregate/raw sub-permissions, so an
  // aggregate-only role (SecOps) holds it and keeps Explore.
  // useHasPermission fails OPEN while caps load / are unavailable, so a
  // pre-RBAC tenant never flash-hides Explore.
  const canAnalytics = useHasPermission(PERMISSIONS.ANALYTICS_EXECUTE);
  const canReadSessions = useHasPermission(PERMISSIONS.AI_SESSIONS_READ);
  const permissionPosture = useMyPermissions();
  const entitlements = useTenantEntitlements();

  // RBAC: the AI chat needs the LLM provider descriptor
  // (tenant.llm_config.read). A role without it (e.g. IT/Fleet Admin) gets
  // a raw 403 out of the chat's creds fetch the moment the console opens —
  // so every chat entry point (the ⌘K singleton, the Dashboard chat
  // sidebar, Explore's chat column) hides instead. Fails OPEN while caps
  // load, same contract as canAnalytics above.
  const canChat = useHasPermission(PERMISSIONS.LLM_CONFIG_READ);

  // Mode is explicit (rail-button state) — derives from ?mode= with
  // a fallback to any legacy ?view= mapping. The active-tab kind no
  // longer determines mode since each mode owns its own tab strip.
  // The param-less default mode — dashboard unless the kill switch reverted
  // the front door to Explore (see dashboardEnabled). A reverted ?mode=dashboard
  // is no longer in VALID_MODES, so it falls through to this default.
  const defaultMode = dashboardEnabled ? "dashboard" : "explore";
  // routeModel.js resolves the mode the URL asks for, including bookmarks from
  // before AI Governance became a first-tier mode (AiServicesAdminPage
  // canonicalizes the URL after render); the analytics layer reads it the same way.
  const resolvedMode = resolveMode(searchParams, { validModes: VALID_MODES, defaultMode, viewModes: SHOWN_VIEW_MODES });
  // The Dashboard home is the default landing. It runs analytics kernels, so a
  // caller without analytics can't use it (nor Explore): both the default
  // landing AND a forced ?mode=dashboard|explore redirect to the first
  // operational surface the caller can use. A Fleet Admin lands in Endpoints;
  // a user with only organization permissions lands in Admin.
  const nonAnalyticsMode = firstVisibleViewForMode("endpoints", permissionPosture, entitlements)
    ? "endpoints"
    : firstVisibleViewForMode("governance", permissionPosture, entitlements)
      ? "governance"
      : "admin";
  const currentMode = (!canAnalytics && (resolvedMode === "workspace" || resolvedMode === "dashboard" || resolvedMode === "explore"))
    ? nonAnalyticsMode
    : resolvedMode;
  // A canvas of traces over the page docks the chat in its own dock, so the
  // page under it lets go of its chat column until it closes.
  const canvasUp = !!useTraceCanvasOpen();
  // Whether the workspace's pane worked in shows the overview already: the
  // rail's Workspace button, pressed in the workspace, goes back to it.
  const overviewInFront = useDeckBlankInFront();

  // Endpoints and Admin have no tab strip: one selected child at a time.
  const singleViewMode = currentMode === "endpoints" || currentMode === "governance" || currentMode === "admin" || currentMode === "security";
  const requestedView = requestedViewFor(searchParams, currentMode, SHOWN_VIEW_MODES);
  const modeView = singleViewMode
    ? (requestedView || firstVisibleViewForMode(currentMode, permissionPosture, entitlements) || (currentMode === "admin" ? "account" : null))
    : null;

  // Resolve the admin view actually rendered in the main canvas:
  // Studio mode reads from its active tab; Endpoints/Admin read their
  // single ?adminView=. Explore has no admin view.
  const adminView = currentMode === "studio" ? studioView
    : singleViewMode ? modeView
    : null;

  const menuOpen = currentMode !== "explore";

  // ── Overview pivot ──
  // Contextual rail item (slides in between Explore and Library while you're
  // in Explore) that full-screens the activity surface and snaps back. With
  // a home pinned (overviewHome.js) it first jumps to that higher-level
  // scope+lens, keeping a dedicated home tab open so flipping back and forth
  // is cheap. `pivoted` mirrors the *actual* expand state of the focus
  // column (synced from ContentShell's columnexpandchange), so the button
  // lights up however the surface got full-screened.
  const [pins, setPinsState] = useState(getPins);
  useEffect(() => {
    const refresh = () => setPinsState(getPins());
    refresh();
    return subscribePins(refresh);
  }, []);
  // ?pin marks WHICH pin we're currently pivoted to (its `lens~scope` token,
  // non-empty even for the overview scope). A ?pin whose scope isn't actually
  // in ?pins — a shared link to a since-removed pin, etc. — would otherwise
  // leave ?xcol cover-expanding the WORKING surface (you never expanded it).
  // Strip both synchronously, before ContentShell's mount reads ?xcol.
  const stalePinCleanedRef = useRef(false);
  const activePinAtMount = parsePinToken(searchParams.get("pin"));
  const stalePin = searchParams.get("pin") != null && (!activePinAtMount || !isPinned(activePinAtMount.scope));
  if (stalePin && !stalePinCleanedRef.current) {
    stalePinCleanedRef.current = true;
    writeUrlParam("pin", null);
    writeUrlParam("xcol", null);
  }
  const [pivoted, setPivoted] = useState(() => !stalePin && searchParams.get("xcol") === "focus");
  // Only the activity surface re-scopes to the active pin (the rest of the
  // page is untouched); null = no pivot (surface uses the page scope).
  // Rehydrate on reload/shared-link from ?pin (the active pin's scope), so
  // ?xcol restores the pinned view rather than expanding the working surface.
  const [surfaceScopeOverride, setSurfaceScopeOverride] = useState(() => {
    if (stalePin || !activePinAtMount) return null;
    return decodeScopePath(activePinAtMount.scope) || [];
  });
  // "Pin mode" — we're showing a pinned scope full-screen. Distinct from
  // `pivoted` (any focus-column expand, incl. a plain in-surface ⤢ expand):
  // the pin UI (rail pins active, Explore de-highlight) keys off THIS.
  const inPinMode = surfaceScopeOverride != null;
  // The encoded scope of the pin we're currently pivoted to (the active one).
  const activePinScope = inPinMode ? encodeScopePath(surfaceScopeOverride) : null;
  // Persist the active pin to the URL (its token) so reload rehydrates it.
  useEffect(() => {
    if (inPinMode) {
      writeUrlParam("pin", pinToken({ scope: activePinScope, lens: getPin(activePinScope)?.lens || "endpoints" }));
    } else {
      writeUrlParam("pin", null);
    }
  }, [inPinMode, activePinScope]);
  // Mirror the focus column's real expand state. ContentShell fires
  // columnexpandchange on the [data-shell] element and the event does NOT
  // bubble — so we listen on window in the CAPTURE phase, which still
  // traverses down to the target. (Listening on shellRef would miss it: the
  // ref is null on the first mount effect, before the shell renders.)
  useEffect(() => {
    const onChange = (e) => {
      const focused = e.detail === "focus";
      setPivoted(focused);
      // Whenever the surface collapses — by ANY means (rail button, the
      // in-surface ×, or Escape) — drop the scope override so the surface
      // never stays "locked" on a pinned scope after exit. Deferred a frame
      // so the content doesn't swap mid-collapse.
      if (!focused) requestAnimationFrame(() => setSurfaceScopeOverride(null));
    };
    window.addEventListener("columnexpandchange", onChange, true);
    return () => window.removeEventListener("columnexpandchange", onChange, true);
  }, []);

  // ── Local UI state ──
  // Mount ScopeColumns once (hidden) and keep it alive — avoids expensive remount on toggle
  const [columnsReady, setColumnsReady] = useState(false);
  // activityLens / endpointTab / sidebar tree state all round-trip
  // through the URL so a copy-pasted link restores the full browser
  // context. (Theme + sidebar width are settings — those stay in
  // localStorage by design.)
  // The Cluster swimlane lens ("clusters") is a gated comparison surface; coerce
  // a stale ?lens=clusters back to the default when it's off so the activity
  // surface never builds the (hidden, heavy) swimlane plotConfig.
  const [activityLens, setActivityLens] = useState(
    () => coerceComparisonLens(searchParams.get("lens") || "endpoints", comparisonsEnabled()),
  );
  // Dimension-level lens focus (the SIGNALS alert console, etc.) + the active lens
  // within the current scope/dimension (a lens id, e.g. "value.skills"). Both are
  // DERIVED from the URL — not useState — so back/forward restore them: the URL is
  // the single source of truth, exactly like tabs/scope (the useMemo above), and
  // there is no state→URL mirror effect to desync. Navigations write ?focus= /
  // ?view= via navigateFocusDim / navigateView / updateScopePath, which
  // mergeUrlParams coalesces into ONE pushed history entry per click. null = no
  // focus (the resolver falls through to value/scope lenses) / the set's default.
  const focusDim = searchParams.get("focus") || null;
  const view = searchParams.get("view") || null;
  const [endpointTab, setEndpointTab] = useState(() => searchParams.get("tab") || "attribution");
  // Trace-log-vs-execution-log tab on every scope page. Hoisted to App
  // for the same reason activityLens lives here — copy-pasted links need
  // to land the reader on whichever log they were reading. URL token
  // values mirror what the log-strip click handlers already use:
  // "sessions" → Trace log at the session grain (the default, so it drops
  // out of the URL), "requests" → Trace log at the request grain,
  // "commands" → Execution log.
  const [logTab, setLogTab] = useState(() => {
    const raw = searchParams.get("log");
    return raw === "commands" || raw === "requests" ? raw : "sessions";
  });

  // Scope steer — a query-language refinement layered ON TOP of the current
  // scope (drives the sidebar facet counts, the dimension entries, and the page
  // views; the trace-window query stacks beneath it). Per-level + accumulating,
  // persisted in ?steers= (see below).
  // Per-level scope steers: prefix-key (encodeScopePath of the scope at that
  // drill level) → query text. Accumulates down the drill — a level's effective
  // steer ANDs every steer on the path above it. Prefix-keyed (not depth) so a
  // steer for an abandoned branch never bleeds onto a different selection.
  const [steers, setSteers] = useState(() => {
    try { return JSON.parse(searchParams.get("steers") || "{}") || {}; } catch { return {}; }
  });
  const setSteer = useCallback((prefixKey, text) => {
    setSteers((prev) => {
      const next = { ...prev };
      if (text && text.trim()) next[prefixKey] = text;
      else delete next[prefixKey];
      return next;
    });
  }, []);
  // Prefix keys from the root down to (and including) the current full scope.
  const steerPrefixKeys = useMemo(
    () => Array.from({ length: scopePath.length + 1 }, (_, L) => encodeScopePath(scopePath.slice(0, L))),
    [scopePath],
  );
  // Effective steer for the CURRENT scope (drives the page views/traces): every
  // steer on the path, ANDed.
  const effectiveSteer = useMemo(
    () => andQueriesAll(steerPrefixKeys.map((k) => steers[k])),
    [steerPrefixKeys, steers],
  );
  const [deferredSteer] = useDebouncedValue(effectiveSteer, 500);
  const steerApplied = useMemo(() => pipelineFilterText(deferredSteer), [deferredSteer]);
  // A value-autocomplete factory bound to a given level's scope prefix — so each
  // level's steer suggests against ITS OWN scope, not the deepest selection.
  const makeSteerSuggest = useCallback(
    (prefixScope) => (fieldKey, term) => suggestQueryValues(fieldKey, term, { scopePath: prefixScope, range }),
    [range],
  );

  // Sidebar tree cache (open/closed, search query, sort, per-entry
  // expansions per dim pane). cacheRef.current is the in-memory store,
  // mirrored to ?ui= (debounced) so a deep link reproduces every
  // expansion the user made. ScopeSidebar normally owns this; we hoist
  // it to App so the URL sync lives at the same level as scope/range.
  const cacheRef = useRef(null);
  if (cacheRef.current == null) {
    try {
      const raw = searchParams.get("ui");
      cacheRef.current = (raw && decodeUrlState(raw)) || {};
    } catch { cacheRef.current = {}; }
  }
  // Bumped by CACHE_NOTIFY on every useCachedState write; drives the
  // URL-sync effect below.
  const [cacheTick, setCacheTick] = useState(0);
  cacheRef.current[CACHE_NOTIFY] = () => setCacheTick((t) => t + 1);
  const [sidebarWidth, setSidebarWidth] = useState(() =>
    readStoredNumber(SIDEBAR_STORAGE_KEY, DEFAULT_SIDEBAR_WIDTH),
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => initialSidebarCollapsed());
  const workspaceSidebar = useRef(null);
  useEffect(() => {
    const onWorkspace = (event) => {
      if (event.detail) setSidebarCollapsed((current) => { if (workspaceSidebar.current === null) workspaceSidebar.current = current; return true; });
      else if (workspaceSidebar.current !== null) { setSidebarCollapsed(workspaceSidebar.current); workspaceSidebar.current = null; }
    };
    window.addEventListener("origin-investigation-workspace", onWorkspace);
    return () => window.removeEventListener("origin-investigation-workspace", onWorkspace);
  }, []);
  const [theme, setTheme] = useState(() => initialTheme());
  // Bumped by resetPanelLayout() to force the explore canvas (SplitPage +
  // ContentShell) to remount so it re-reads freshly-cleared layout state.
  const [layoutNonce, setLayoutNonce] = useState(0);
  useEffect(() => subscribeItemLinkRecovery(() => {
    // The clean URL alone cannot clear ScopedPage's in-memory tab cache.
    // Reset the URL-backed selection and remount it so returning to Explore
    // cannot resurrect the unavailable trace that initiated recovery.
    setEndpointTab("attribution");
    setLayoutNonce((n) => n + 1);
  }), []);

  // Pre-mount ScopeColumns hidden during idle so expand is instant
  useEffect(() => {
    if (columnsReady) return;
    const id = typeof requestIdleCallback !== "undefined"
      ? requestIdleCallback(() => setColumnsReady(true), { timeout: 3000 })
      : setTimeout(() => setColumnsReady(true), 1000);
    return () => { typeof cancelIdleCallback !== "undefined" ? cancelIdleCallback(id) : clearTimeout(id); };
  }, [columnsReady]);


  // ── Persistence ──
  useEffect(() => {
    persistShellState({ sidebarWidth, sidebarCollapsed, theme });
  }, [sidebarWidth, sidebarCollapsed, theme]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.body.dataset.theme = theme;
  }, [theme]);

  // Subscribe to label-cache updates so the document title catches up
  // once the deepest step's analytics label arrives.
  useLabels(scopePath);
  // Off-band hydration for adapter-backed dims whose scope step
  // resolves into other dims (e.g. a Linear ticket → endpoint set +
  // time window). Fires `adapter.hydrateScopeStep` once per step
  // appearance; the adapter's notify() bumps state when resolution
  // completes; toApiScope picks up `rewriteScopeStep` output on the
  // re-render. No-op when no adapter step is in scope.
  useScopeHydration(scopePath, range);
  const scopeLabel = scopePath.length
    ? scopeStepLabel(scopePath[scopePath.length - 1])
    : "Overview";
  useEffect(() => {
    document.title = `Origin / ${scopeLabel}`;
  }, [scopeLabel]);


  // Reset context tab when scope changes — but preserve any pinned-snapshot
  // tab (cell / trace / command). Those carry their own cached data and the
  // user is typically navigating *from* them; keeping them active lets them
  // orient themselves against the new scope. Only the scope-bound overview
  // ("attribution") is reset.
  const scopeKey = encodeScopePath(scopePath);
  useEffect(() => {
    setEndpointTab((cur) => {
      if (!cur) return "attribution";
      if (
        cur.startsWith("cell:") ||
        cur.startsWith("trace:") ||
        cur.startsWith("command:")
      ) return cur;
      return "attribution";
    });
  }, [scopeKey]);

  // Push-coalescing URL-param writer every navigation routes through: reads fresh
  // from the URL (never react-router's stale `prev`), and coalesces the several
  // pushes one click fires (scope + focus + view) into ONE back/forward stop.
  // See useUrlParamMerger for the full rationale + the microtask coalescing window.
  const mergeUrlParams = useUrlParamMerger(setSearchParams);

  // The cluster generation pin lives in the `gen` URL param (read by
  // analyticsClient.runQuery). When it changes — picker click, a shared
  // link, or back/forward — fire the generationPin channel so the caches
  // that can't see the URL (clusterTree, cluster labels, dataClient's
  // result cache + epoch) invalidate. Skips the initial value so a fresh
  // load with a pin doesn't needlessly clear cold caches.
  const prevGenRef = useRef(searchParams.get(GEN_PARAM) || null);
  useEffect(() => {
    const gen = searchParams.get(GEN_PARAM) || null;
    if (gen !== prevGenRef.current) {
      prevGenRef.current = gen;
      notifyGenerationPinChange();
    }
  }, [searchParams]);

  // Mirror endpointTab into the URL ?tab= so trace deep links work.
  useEffect(() => {
    mergeUrlParams((next) => {
      if (endpointTab && endpointTab !== "attribution") next.set("tab", endpointTab);
      else next.delete("tab");
    });
  }, [endpointTab, setSearchParams]);

  // Mirror activityLens to ?lens=. Default ("endpoints") drops out so
  // a fresh URL stays clean.
  useEffect(() => {
    mergeUrlParams((next) => {
      if (activityLens && activityLens !== "endpoints") next.set("lens", activityLens);
      else next.delete("lens");
    });
  }, [activityLens, setSearchParams]);

  // NB: focusDim / view are DERIVED from the URL (see their declaration), so they
  // have no state→URL mirror effect — navigations write ?focus= / ?view= directly
  // (navigateFocusDim / navigateView / updateScopePath), and back/forward restore
  // them by re-deriving from the restored URL.

  // Mirror logTab to ?log=. Default ("sessions") drops out — same
  // clean-URL discipline as ?lens=.
  useEffect(() => {
    mergeUrlParams((next) => {
      if (logTab && logTab !== "sessions") next.set("log", logTab);
      else next.delete("log");
    });
  }, [logTab, setSearchParams]);

  // Mirror the per-level steers to ?steers= so a steered breakdown is shareable
  // / deep-linkable. Persist only steers on the CURRENT path (abandoned-branch
  // entries drop out — keeps the URL clean and bounded). Empty → no param.
  useEffect(() => {
    const pruned = {};
    for (const k of steerPrefixKeys) if (steers[k]) pruned[k] = steers[k];
    mergeUrlParams((next) => {
      if (Object.keys(pruned).length) next.set("steers", JSON.stringify(pruned));
      else next.delete("steers");
    });
  }, [steers, steerPrefixKeys, setSearchParams]);

  // Latest open tabs / pins, read inside the debounced effect below without
  // widening its deps — explorerTabs/pins get a fresh identity on every
  // searchParams change (including our own ?ui= write), so depending on them
  // would re-fire the effect in a churn loop. Refs give fresh values; the
  // effect still fires only on cacheTick.
  const explorerTabsRef = useRef(explorerTabs);
  explorerTabsRef.current = explorerTabs;
  const pinsRef = useRef(pins);
  pinsRef.current = pins;

  // Mirror cacheRef → ?ui=, debounced. Keys with default values
  // (open=null, empty search, default sort, empty entryExp) are pruned
  // before serialisation, as are orphan entries for scope paths no open
  // tab or pin references, so the URL stays bounded by what's on screen.
  useEffect(() => {
    if (cacheTick === 0) return undefined;
    const id = setTimeout(() => {
      const live = liveScopePathsFor(
        [
          ...explorerTabsRef.current.map((t) => scopePathFor(t) || []),
          ...pinsRef.current.map((p) => decodeScopePath(p.scope) || []),
        ],
        encodeScopePath,
      );
      const cleaned = pruneCacheForUrl(cacheRef.current, live);
      const value = Object.keys(cleaned).length ? encodeUrlState(cleaned) : "";
      mergeUrlParams((next) => {
        if (value) next.set("ui", value);
        else next.delete("ui");
      });
    }, 250);
    return () => clearTimeout(id);
  }, [cacheTick, setSearchParams]);

  // ── Route updates ──

  // Strip descriptor — bundles each tab strip's URL params, current
  // state, and "default tab" sentinel into one object so all the
  // mutation helpers below can be generic.
  const EXPLORER_STRIP = {
    paramTabs: "tabs",
    paramActive: "active",
    paramDrop: ["scope"],
    tabs: explorerTabs,
    activeIdx: explorerActiveIdx,
    defaultTab: makeScopeTab([]),
  };
  const STUDIO_STRIP = {
    paramTabs: "studioTabs",
    paramActive: "studioActive",
    paramDrop: ["view", "kernel", "canvas"],
    tabs: studioTabs,
    activeIdx: studioActiveIdx,
    defaultTab: makeStudioTab(),
  };

  // Strip writes are navigations (scope/tab changes) → push a history
  // entry by default. reorderTab opts out (push:false) since shuffling
  // tab order isn't a place you'd want to "go back" to.
  function writeStrip(strip, nextTabs, idx, { push = true } = {}) {
    const encoded = nextTabs.map(encodeTab);
    const defaultEncoded = encodeTab(strip.defaultTab);
    mergeUrlParams((next) => {
      if (encoded.length === 1 && encoded[0] === defaultEncoded) {
        next.delete(strip.paramTabs);
        next.delete(strip.paramActive);
      } else {
        next.set(strip.paramTabs, encoded.join(";"));
        next.set(strip.paramActive, String(idx));
      }
      for (const p of strip.paramDrop) next.delete(p);
    }, { push });
  }

  function updateScopePath(newPath, { focus = null, view: nextView = null, params = null } = {}) {
    // A committed scope change normally leaves any dimension lens / custom view and
    // returns to the new path's default lens; a caller can carry a focus/view INTO
    // the new scope by passing { focus } / { view } — e.g. picking a person in the
    // memory roster lands on their memory store. focus/view are written INTO the
    // SAME pushed history entry as the scope (coalesced by mergeUrlParams) rather
    // than via a state mirror effect that would land on the PREVIOUS entry — so a
    // scope+lens navigation is one back/forward stop and a shareable deep link.
    startTransition(() => {
      const nextTabs = explorerTabs.slice();
      nextTabs[explorerActiveIdx] = makeScopeTab(newPath);
      writeStrip(EXPLORER_STRIP, nextTabs, explorerActiveIdx);
      mergeUrlParams((next) => applyScopeNavParams(next, { focus, view: nextView, params }), { push: true });
    });
  }

  // A user-initiated lens focus / in-scope view switch from the sidebar is a
  // navigation → write ?focus= / ?view= with push so back/forward can step to it.
  // focus/view are URL-derived, so writing the URL is what changes them; the write
  // coalesces with any sibling scope write in the same click into one entry.
  const navigateFocusDim = (dim) => {
    mergeUrlParams((next) => { if (dim) next.set("focus", dim); else next.delete("focus"); }, { push: true });
  };
  const navigateView = (v) => {
    exitDetailFullScreen(() => {
      mergeUrlParams((next) => { if (v) next.set("view", v); else next.delete("view"); }, { push: true });
    });
  };

  // Deep-link hierarchy expansion. A child node in a hierarchical dim (a
  // cluster reached from the anatomy map's goToNode, or a shared/permalink
  // URL) lands as a single scope step — e.g. `clusters:<leaf>` with no
  // ancestors. The nav sidebar opens each level only when that level's step
  // is present (ScopeSidebar's isActiveDim chain), so without the ancestors
  // it can't expand to reveal the node. Rewrite the active explorer scope to
  // its full ancestor chain (root→…→node) once the ancestors are known; the
  // existing chain-open then reveals the whole hierarchy. The deepest step is
  // unchanged, so the queried scope is identical. replace (push:false) so it
  // doesn't add a back-button stop; idempotent, so an already-expanded path
  // re-encodes identically and skips the write. Skipped in pin mode — there
  // the sidebar drives the pinned override, not this working tab.
  const activeScopePayload = explorerTabs[explorerActiveIdx]?.payload ?? "";
  const genParam = searchParams.get(GEN_PARAM) || "";
  useEffect(() => {
    // Skip when the active explorer tab isn't a scope tab (e.g. an
    // influence-trace tab) — this rewrites the active tab as a scope, so
    // running it on a non-scope tab would clobber it back to a scope view.
    if (currentMode !== "explore" || surfaceScopeOverride || influenceTab) return undefined;
    let cancelled = false;
    (async () => {
      const expanded = await expandHierarchicalScope(scopePath);
      if (cancelled || expanded === scopePath) return;
      const expandedPayload = encodeScopePath(expanded);
      if (expandedPayload === activeScopePayload) return;
      const nextTabs = explorerTabs.slice();
      nextTabs[explorerActiveIdx] = { kind: "scope", payload: expandedPayload };
      writeStrip(EXPLORER_STRIP, nextTabs, explorerActiveIdx, { push: false });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeScopePayload, genParam, currentMode, surfaceScopeOverride, influenceTab]);

  // In pin mode the nav sidebar reflects and drives the PINNED scope (the
  // override) rather than the working tab — so you can adjust what you're
  // pinned to from the same place. Navigating updates the live override AND
  // re-pins the home to the new scope (keeping its lens); the working tab is
  // untouched, so exiting pin mode returns you to it. Out of pin mode it's
  // ordinary working-tab navigation.
  const sidebarScopePath = surfaceScopeOverride || scopePath;
  function onSidebarChange(newPath) {
    // clearReplayParam: a scope change REMOUNTS the explorer subtree, and a
    // shell that mounts with ?xcol=detail still set replays the full-screen
    // lift over the new scope. The dispatch still covers the re-scoped-pin
    // path, which does not remount.
    exitDetailFullScreen(() => {
      if (surfaceScopeOverride) {
        // Re-scope the active pin in place (keep its slot + lens) and follow it.
        updatePin(encodeScopePath(surfaceScopeOverride), { scope: encodeScopePath(newPath) });
        setSurfaceScopeOverride(newPath);
      } else {
        updateScopePath(newPath);
      }
    }, { clearReplayParam: true });
  }

  // Jump from an admin/settings/studio surface straight into the
  // Explore canvas at a specific scope. Leaving the current mode and
  // retargeting the active explorer tab MUST happen in a single
  // mergeUrlParams pass: each call rebuilds the URL from
  // window.location, so two sequential writes (selectMode +
  // updateScopePath) would each miss the other's mutation and the last
  // one would clobber the first. The strip-write half mirrors
  // writeStrip(EXPLORER_STRIP, …).
  // `newTab` opens the scope in its own explorer tab beside the active one, or
  // re-activates a tab already on that scope, instead of retargeting the
  // active tab. The alert queue uses it so an analyst's Explore working tab
  // survives a look at a session. It stays an in-app tab: a cold browser tab
  // re-authenticates and drops tab and scope state at the auth boundary.
  function openInExplore(scopePath, { newTab = false } = {}) {
    const target = makeScopeTab(scopePath);
    const nextTabs = explorerTabs.slice();
    let activeIdx = explorerActiveIdx;
    if (newTab) {
      const existing = findTabIndex(nextTabs, target);
      if (existing >= 0) {
        activeIdx = existing;
      } else {
        activeIdx = explorerActiveIdx + 1;
        nextTabs.splice(activeIdx, 0, target);
      }
    } else {
      nextTabs[activeIdx] = target;
    }
    const encoded = nextTabs.map(encodeTab);
    const defaultEncoded = encodeTab(EXPLORER_STRIP.defaultTab);
    startTransition(() => {
      setActivityLens("endpoints");
      mergeUrlParams((next) => {
        // Land in Explore. The param-less default isn't guaranteed to be
        // Explore (see defaultLanding), so SET the mode explicitly rather
        // than relying on a cleared ?mode=.
        next.set("mode", "explore");
        next.delete("lens");
        next.delete("menu");
        next.delete("view");
        next.delete("kernel");
        next.delete("canvas");
        next.delete("settingsView");
        // Write the explorer strip (mirrors writeStrip).
        if (encoded.length === 1 && encoded[0] === defaultEncoded) {
          next.delete(EXPLORER_STRIP.paramTabs);
          next.delete(EXPLORER_STRIP.paramActive);
        } else {
          next.set(EXPLORER_STRIP.paramTabs, encoded.join(";"));
          next.set(EXPLORER_STRIP.paramActive, String(activeIdx));
        }
        for (const p of EXPLORER_STRIP.paramDrop) next.delete(p);
      }, { push: true });
    });
  }

  // ── Overview pivot ──
  // Take over the constant-size activity surface and full-screen it — no tab
  // swap, no scope change, nothing around it re-renders. The surface alone
  // re-scopes to the home (via surfaceScopeOverride) and ContentShell runs
  // the same FLIP the in-surface expand button uses. Dispatched SYNCHRONOUSLY
  // in the click so the FLIP captures the real source rect (smooth).
  const commandFocusColumn = (mode) => {
    // full: true — the pivot expands the surface OVER the tab strip (cover the
    // header) so it grows up + out with no layout shift, rather than removing
    // the strip from flow.
    window.dispatchEvent(new CustomEvent("origin-column-expand", {
      detail: { columnId: "focus", mode, full: true },
    }));
  };

  // Pivot the surface to a given scope (drive the override, then FLIP a frame
  // later once it's re-rendered at that scope+lens — same tick detaches the
  // element mid-FLIP and it snaps).
  function pivotToScope(scopePath) {
    setSurfaceScopeOverride(scopePath || []);
    requestAnimationFrame(() => commandFocusColumn("expand"));
  }

  function pivotBack() {
    // Just collapse — the columnexpandchange listener clears the override on
    // collapse (covers the in-surface × and Escape too, not only this path).
    commandFocusColumn("collapse");
  }

  // Toggle a specific pin from the rail: if it's the active one, snap back;
  // otherwise flip to it.
  function togglePin(pin) {
    if (inPinMode && activePinScope === pin.scope) pivotBack();
    else pivotToScope(decodeScopePath(pin.scope) || []);
  }

  // Pinning a scope (⌂ on a surface) drops you straight into it full-screen.
  // The ⌂ adds the pin then dispatches origin-activate-home with the scope;
  // we flip to it. (Explicit event — never auto-fires on initial load.)
  useEffect(() => {
    const onActivate = (e) => {
      const scope = e?.detail?.scope;
      pivotToScope(scope != null ? (decodeScopePath(scope) || []) : []);
    };
    window.addEventListener("origin-activate-home", onActivate);
    return () => window.removeEventListener("origin-activate-home", onActivate);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // While pivoted, lens changes lock to the ACTIVE pin (its own remembered
  // tab) instead of the global/working lens — so pinned surfaces and the
  // working surface don't bleed tabs into each other.
  const setHomeLens = (lens) => {
    if (activePinScope != null) updatePin(activePinScope, { lens });
  };

  // If the active pin is removed (unpinned) while we're pivoted to it, restore
  // the regular explore state: collapse (which clears the override via the
  // columnexpandchange listener). Without this the surface would stay stuck
  // full-screen on the now-removed scope with no rail control to exit.
  useEffect(() => {
    if (surfaceScopeOverride && !isPinned(encodeScopePath(surfaceScopeOverride))) {
      commandFocusColumn("collapse");
    }
  }, [pins, surfaceScopeOverride]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset the chrome layout to defaults — sidebar width + collapse, the
  // detail/context panel width + collapse, and any full-width column
  // expansion. Persisted layout lives in three places: React state
  // (sidebar), a module-level var (detail width), and hash params
  // (dcol/xcol). We clear all three, then bump layoutNonce to remount the
  // explore canvas so the mounted SplitPage/ContentShell re-read the
  // cleared defaults (they only read width/dcol/xcol at mount).
  function resetPanelLayout() {
    setSidebarWidth(DEFAULT_SIDEBAR_WIDTH);
    setSidebarCollapsed(false);
    resetSharedDetailPanelWidth();
    // Also fully exit pin mode — otherwise the remount collapses the surface
    // but the override/pivoted/?pin state lingers, leaving the rail pin lit
    // with nothing expanded. Clear the override (drops ?pin via its mirror)
    // and the synced pivoted flag.
    setSurfaceScopeOverride(null);
    setPivoted(false);
    // Synchronous (history.replaceState), so the values are gone before
    // the remount below re-reads them.
    writeUrlParam("dcol", null);
    writeUrlParam("xcol", null);
    writeUrlParam("pin", null);
    setLayoutNonce((n) => n + 1);
  }

  // Open (or activate-existing) a tab. Strip is inferred from the
  // tab kind — scope→explorer, anything else→studio. `forceNew`
  // bypasses the activate-existing dedupe so the explicit "+" button
  // always adds a fresh tab — without it, hitting "+" while already on
  // the blank Overview scope just re-activates that identical [] tab
  // and nothing appears to happen.
  function openTab(newTab, { forceNew = false } = {}) {
    const strip = (newTab.kind === "scope" || newTab.kind === "influence") ? EXPLORER_STRIP : STUDIO_STRIP;
    if (!forceNew) {
      const existing = findTabIndex(strip.tabs, newTab);
      if (existing >= 0) {
        writeStrip(strip, strip.tabs, existing);
        return;
      }
    }
    const insertAt = strip.activeIdx + 1;
    const nextTabs = [...strip.tabs.slice(0, insertAt), newTab, ...strip.tabs.slice(insertAt)];
    writeStrip(strip, nextTabs, insertAt);
  }

  // Open a brand-new influence-trace tab in the explore strip for a turn
  // of a trace. forceNew so re-tracing the same turn always adds a fresh
  // tab rather than re-activating an existing one. The seq keeps each tab
  // distinct even for an identical (trace, turn).
  function openInfluenceTab(traceId, turnIndex) {
    if (!traceId) return;
    influenceSeqRef.current += 1;
    openTab(makeInfluenceTab({ traceId, turnIndex, seq: influenceSeqRef.current }), { forceNew: true });
  }

  function closeTab(strip, idx) {
    if (strip.tabs.length <= 1) {
      writeStrip(strip, [strip.defaultTab], 0);
      return;
    }
    const nextTabs = strip.tabs.filter((_, i) => i !== idx);
    let newActive = strip.activeIdx;
    if (idx === strip.activeIdx) newActive = Math.min(idx, nextTabs.length - 1);
    else if (idx < strip.activeIdx) newActive = strip.activeIdx - 1;
    writeStrip(strip, nextTabs, newActive);
  }

  function activateTab(strip, idx) {
    if (idx === strip.activeIdx) return;
    writeStrip(strip, strip.tabs, idx);
  }

  function reorderTab(strip, fromIdx, toIdx) {
    if (fromIdx === toIdx) return;
    if (fromIdx < 0 || fromIdx >= strip.tabs.length) return;
    if (toIdx < 0 || toIdx >= strip.tabs.length) return;
    const nextTabs = strip.tabs.slice();
    const [moved] = nextTabs.splice(fromIdx, 1);
    nextTabs.splice(toIdx, 0, moved);
    let newActive = strip.activeIdx;
    if (strip.activeIdx === fromIdx) newActive = toIdx;
    else if (fromIdx < strip.activeIdx && strip.activeIdx <= toIdx) newActive = strip.activeIdx - 1;
    else if (toIdx <= strip.activeIdx && strip.activeIdx < fromIdx) newActive = strip.activeIdx + 1;
    writeStrip(strip, nextTabs, newActive, { push: false });
  }

  // Per-step actions used by the ▾ menu on the active tab. Scope-tab-
  // only — guarded since the menu chevron is gated on a scope tab.
  function jumpStep(stepIdx) {
    updateScopePath(scopePath.slice(0, stepIdx + 1));
  }
  function openStepInNew(stepIdx) {
    openTab(makeScopeTab(scopePath.slice(0, stepIdx + 1)));
  }
  function dropStep(stepIdx) {
    updateScopePath(scopePath.filter((_, i) => i !== stepIdx));
  }

  function updateRange(value) {
    mergeUrlParams((next) => {
      next.set("range", value);
    });
  }

  // Rail mode picker. Per-mode tab strips mean we don't need to find
  // or open a tab — each mode's tab strip preserves its own state
  // independently. Just flip ?mode= and let each mode render its own
  // strip + selection.
  function selectMode(modeId) {
    if (!VALID_MODES.has(modeId)) return;
    // Explore is hidden + off-limits without analytics access; ignore a
    // stray request to enter it (e.g. a keyboard shortcut) rather than
    // routing to a surface the role can't use.
    if (modeId === "explore" && !canAnalytics) return;
    // Always exit an active overview pivot when switching modes — clicking
    // Explore (or any mode) returns you to the regular, un-expanded view; the
    // collapse listener drops the scope override. This is the safety valve
    // that guarantees you can never get stuck in the pinned/full-screen state.
    if (pivoted) commandFocusColumn("collapse");
    mergeUrlParams((next) => {
      // Always drop legacy single-source params that fight the new
      // mode-aware routing.
      next.delete("menu");
      next.delete("view");
      next.delete("kernel");
      next.delete("canvas");
      // The resolved default landing (defaultLanding()) is the param-less
      // mode, so it's the one that clears ?mode=; every other mode sets it
      // explicitly. Tracking defaultMode (not a hardcoded "dashboard") keeps
      // the rail buttons working when the front door is reverted to Explore.
      if (modeId === defaultMode) {
        next.delete("mode");
      } else {
        next.set("mode", modeId);
      }
      // Endpoints, AI Governance, and Admin have no app-level tab strip —
      // seed a permitted child view.
      if (modeId === "endpoints" || modeId === "governance" || modeId === "admin") {
        const existing = next.get("adminView");
        // Feedback has its own rail entry, so Admin returns to its menu.
        if (!existing || existing === "report" || modeForView(existing) !== modeId) {
          const fallback = firstVisibleViewForMode(modeId, permissionPosture, entitlements)
            || (modeId === "admin" ? "account" : "");
          next.set("adminView", fallback);
        }
        next.delete("settingsView");
      }
      // A skills-only role has no usable Library overview or analytics
      // artifacts. Land directly on the one Library surface it can use.
      if (modeId === "studio" && !canAnalytics && ADMIN_VIEW_IDS.has("skills")) {
        next.set("studioTabs", encodeTab(makeSkillsTab()));
        next.set("studioActive", "0");
      }
    }, { push: true });
  }

  // Child-view picker for the Endpoints, AI Governance, and Admin workspaces.
  // These modes don't carry an app-level tab strip, so each click replaces
  // the single active view through ?adminView=. Library uses its typed tab
  // strip instead.
  function selectAdminView(viewId) {
    if (!viewId || !ADMIN_VIEW_IDS.has(viewId)) return;
    const mode = modeForView(viewId) || "admin";
    mergeUrlParams((next) => {
      next.set("mode", mode);
      if (mode === "endpoints" || mode === "governance" || mode === "admin" || mode === "security") next.set("adminView", viewId);
      next.delete("settingsView");
      next.delete("menu");
      next.delete("view");
    }, { push: true });
  }

  // Map a studio view + item id to its tab descriptor. Shared by the
  // in-Studio sidebar picker (selectStudioItem) and the external opener
  // (openStudioItemExternal) so both agree on tab shape. A missing item
  // id falls back to the overview tab.
  function studioTabFor(view, itemId) {
    if (view === "kernels") return itemId ? makeKernelTab(itemId) : makeStudioTab();
    if (view === "canvases") return itemId ? makeCanvasTab(itemId) : makeStudioTab();
    // Workflow / signal detail tabs (workflow config + run history;
    // trigger definition + run/annotation log), keyed by command —
    // parallel the kernel/canvas detail tabs.
    if (view === "workflows") return itemId ? makeWorkflowTab(itemId) : makeStudioTab();
    if (view === "signals") return itemId ? makeTriggerTab(itemId) : makeStudioTab();
    if (view === "skills") return makeSkillsTab();
    return makeStudioTab();
  }

  // Studio sidebar item picker. Opens / activates a tab in the
  // Studio strip — doesn't touch the Explore strip at all. The
  // sidebar lives inside Studio mode so we don't need to flip ?mode=
  // explicitly (it's already studio).
  function selectStudioItem(view, itemId) {
    openTab(studioTabFor(view, itemId));
  }

  // Follow a studio item that changed its own id — today only a canvas slug
  // edit. The tab holding the old id BECOMES the new one, keeping its position
  // and active state. Opening a tab instead would strand the original on an id
  // that no longer resolves, which renders as the library landing.
  function renameStudioItem(view, fromItemId, toItemId) {
    if (!fromItemId || !toItemId || fromItemId === toItemId) return;
    const strip = STUDIO_STRIP;
    const renamed = studioTabFor(view, toItemId);
    const moved = renameTab(strip.tabs, studioTabFor(view, fromItemId), renamed);
    if (!moved) {
      openTab(renamed);
      return;
    }
    writeStrip(strip, moved.tabs, moved.activeIdx);
    // A dashboard-home tab opened for the old slug goes stale the same way.
    if (view === "canvases") {
      setHomeTabs((cur) => renameHomeTab(cur, "canvas", fromItemId, toItemId));
    }
  }

  // Drop the tab for a studio item that stopped existing. A delete leaves its
  // tab behind otherwise, pointing at an id nothing resolves, which renders as
  // the library landing. closeTab already picks the neighbour to land on and
  // falls back to the overview when this was the last tab.
  function closeStudioItem(view, itemId) {
    if (!itemId) return;
    const strip = STUDIO_STRIP;
    const idx = findTabIndex(strip.tabs, studioTabFor(view, itemId));
    if (idx >= 0) closeTab(strip, idx);
    if (view === "canvases") setHomeTabs((cur) => removeHomeTab(cur, "canvas", itemId));
  }

  // Open a studio item from OUTSIDE studio — the chat overlay's
  // post-save "view canvas" / "view kernel" buttons. Same target tab as
  // selectStudioItem, but the chat floats over ANY mode, so this also
  // flips ?mode=studio. The mode flip + studio-strip write MUST be one
  // mergeUrlParams pass: each call rebuilds the URL from
  // window.location, so two sequential writes would clobber each other
  // (the same single-pass constraint openInExplore documents). The
  // activate-existing-or-insert half mirrors openTab() inline so it can
  // share that one write. Without this, the buttons wrote legacy
  // ?view=…&canvas/kernel=… params that the mode-aware router ignored
  // when a mode/studioTabs param was already present — so the button
  // appeared to do nothing.
  function openStudioItemExternal(view, itemId) {
    const strip = STUDIO_STRIP;
    const newTab = studioTabFor(view, itemId);
    const existing = findTabIndex(strip.tabs, newTab);
    let nextTabs, idx;
    if (existing >= 0) {
      nextTabs = strip.tabs;
      idx = existing;
    } else {
      const insertAt = strip.activeIdx + 1;
      nextTabs = [...strip.tabs.slice(0, insertAt), newTab, ...strip.tabs.slice(insertAt)];
      idx = insertAt;
    }
    const encoded = nextTabs.map(encodeTab);
    const defaultEncoded = encodeTab(strip.defaultTab);
    startTransition(() => {
      mergeUrlParams((next) => {
        next.set("mode", "studio");
        if (encoded.length === 1 && encoded[0] === defaultEncoded) {
          next.delete(strip.paramTabs);
          next.delete(strip.paramActive);
        } else {
          next.set(strip.paramTabs, encoded.join(";"));
          next.set(strip.paramActive, String(idx));
        }
        for (const p of strip.paramDrop) next.delete(p);
        next.delete("menu");
      }, { push: true });
    });
  }

  // Explore analog of openStudioItemExternal: open a data-graph scope as
  // a tab in the MAIN column and land in Explore — used by the chat's
  // navigation tool / affordances (navigationStore) and the render_block
  // "open scope" affordance. Lands in Explore by SETTING ?mode=explore
  // (Canvases is the param-less default now) + dropping the other modes'
  // selection params in the same single pass that writes the explorer strip. Open-or-
  // activate: an identical scope tab is re-activated, not duplicated.
  // `view` optionally lands on a specific lens (e.g. "scope.canvases")
  // instead of the scope's default; `cv` selects a canvas within it —
  // the scout profile view's tailored-canvas entries use both.
  function openExploreScopeExternal(scopePath, { view = null, cv = null } = {}) {
    const strip = EXPLORER_STRIP;
    const newTab = makeScopeTab(scopePath || []);
    const existing = findTabIndex(strip.tabs, newTab);
    let nextTabs, idx;
    if (existing >= 0) {
      nextTabs = strip.tabs;
      idx = existing;
    } else {
      const insertAt = strip.activeIdx + 1;
      nextTabs = [...strip.tabs.slice(0, insertAt), newTab, ...strip.tabs.slice(insertAt)];
      idx = insertAt;
    }
    const encoded = nextTabs.map(encodeTab);
    const defaultEncoded = encodeTab(strip.defaultTab);
    startTransition(() => {
      setActivityLens("endpoints");
      mergeUrlParams((next) => {
        // Set Explore explicitly — the param-less default isn't guaranteed
        // to be Explore (see defaultLanding), so a cleared ?mode= could land
        // on the Dashboard instead.
        next.set("mode", "explore");
        next.delete("lens");
        next.delete("menu");
        if (view) next.set("view", view); else next.delete("view");
        // Clear a stale ?cv= like ?view= above — leaving it would silently
        // reopen an earlier canvas next time the Canvases lens is selected.
        if (cv) next.set("cv", cv); else next.delete("cv");
        next.delete("kernel");
        next.delete("canvas");
        if (encoded.length === 1 && encoded[0] === defaultEncoded) {
          next.delete(strip.paramTabs);
          next.delete(strip.paramActive);
        } else {
          next.set(strip.paramTabs, encoded.join(";"));
          next.set(strip.paramActive, String(idx));
        }
        for (const p of strip.paramDrop) next.delete(p);
      }, { push: true });
    });
  }

  // Open a saved / tailored canvas WHERE THE USER ALREADY IS rather than
  // yanking them to Explore's root: on the Dashboard home the canvas
  // becomes the home selection (selectCanvasHome exits the docked chat and
  // pins ?cv=); in any other mode it opens the Canvases lens at the
  // explorer's CURRENT scope, so scoped kernels keep the drilldown
  // narrowing. Used by the scout profile view's tailored-canvas entries.
  // (selectCanvasHome is defined below; this only runs at event time.)
  function openCanvasExternal(slug) {
    if (!slug) return;
    if (currentMode === "dashboard") {
      selectCanvasHome(slug);
      return;
    }
    openExploreScopeExternal(scopePath, { view: "scope.canvases", cv: slug });
  }

  // Open a conversation as a trace tab on one of its readings, wearing a chat's
  // knowledge graph (lib/traceGraphOverlay.js): the chat's door into the traces
  // its graph names. The tab hydrates from its key, in whatever scope the
  // explorer is on (ScopedPage's deep-link path), and the context column is
  // brought forward, since a tab that is already open has nothing to hydrate.
  function openTraceExternal({ traceId, reading = "lanes", graph = null, claim = null, expand = false }) {
    if (!traceId) return;
    const navigate = () => startTransition(() => {
      setEndpointTab(`trace:${traceId}`);
      mergeUrlParams((next) => {
        applyTraceNavParams(next, { reading, graph, claim, expand });
        // A finding named for another conversation means nothing here either.
        next.delete(FINDING_FOCUS_PARAM);
      }, { push: true });
    });
    if (expand) enterDetailFullScreen(navigate);
    else navigate();
    window.dispatchEvent(new CustomEvent("origin-context-activate", { detail: { tab: `trace:${traceId}` } }));
  }

  // Apply navigation intents the chat publishes on navigationStore (the
  // agent's open_in_explore / open_studio_item tools and their in-
  // transcript affordances). Held in a ref so the single subscription
  // below always invokes the latest closures — openExploreScopeExternal /
  // openStudioItemExternal are recreated each render.
  const applyNavigationRef = useRef(null);
  applyNavigationRef.current = (intent) => {
    if (!intent) return;
    if (intent.kind === "explore") openExploreScopeExternal(intent.scopePath, { view: intent.view, cv: intent.cv });
    else if (intent.kind === "canvas") openCanvasExternal(intent.slug);
    else if (intent.kind === "studio") openStudioItemExternal(intent.view, intent.itemId);
    else if (intent.kind === "trace") openTraceExternal(intent);
  };
  useEffect(() => subscribeNavigation((intent) => applyNavigationRef.current?.(intent)), []);

  // Create a blank workflow, then open its detail tab. Async because
  // the workflow store is IndexedDB-backed (unlike the localStorage
  // kernel/canvas stores); the sidebar list refreshes via its own
  // subscribeWorkflows listener once the save lands.
  async function createWorkflow() {
    try {
      const tenantId = studioInventoryTenantIdRef.current;
      if (AUTH_REQUIRED && !tenantId) return;
      const wf = await createBlankWorkflow(tenantId);
      if (wf?.command) openTab(makeWorkflowTab(wf.command));
    } catch (err) {
      console.warn("[workflows] create failed:", err);
    }
  }

  // Live kernel + canvas inventory for the Studio sidebar. Listening
  // here (vs inside StudioMenu) keeps the data wired even if the
  // sidebar is collapsed — so when the user expands it, the lists are
  // already current rather than triggering a fresh load on open.
  const [studioKernels, setStudioKernels] = useState(() => (AUTH_REQUIRED ? [] : listKernels(null)));
  useEffect(() => subscribeKernels(() => {
    const tenantId = studioInventoryTenantIdRef.current;
    setStudioKernels(AUTH_REQUIRED && !tenantId ? [] : listKernels(tenantId));
  }), []);
  const [studioCanvases, setStudioCanvases] = useState(() => (AUTH_REQUIRED ? [] : listCanvasTemplates(null)));
  const [studioLegacyImportReport, setStudioLegacyImportReport] = useState(null);
  const studioLegacyImportIdentityRef = useRef(null);
  const studioInventoryTenantIdRef = useRef(null);
  const [studioInventoryHydrated, setStudioInventoryHydrated] = useState(false);
  const [studioInventoryTenantId, setStudioInventoryTenantId] = useState(undefined);
  const studioCloudState = useSyncExternalStore(
    subscribeTenantViewCloud,
    () => getTenantViewCloudState(null),
    () => getTenantViewCloudState(null),
  );

  // Dashboard home (rail mode "dashboard", the default landing). Selection
  // rides ?cv=<slug>, shared with the scope.canvases lens; falls back to the
  // first canvas (built-ins first). The "dashboard" MODE id is separate from
  // the studio "canvases" ADMIN VIEW (different namespaces; see resolvedMode).
  //
  // CANVAS_HOME_LAYOUT: "hub" = the single-column "for dummies" view (canvas
  // list + chat history + New chat in the sidebar; canvas OR docked chat in the
  // main column). "overlay" = canvases page + chat in the ⌘K overlay (prior
  // behavior). Build-time design choice (no runtime lever) — switching it is a
  // rebuild. To revert the whole front door to Explore without a rebuild, use
  // the defaultLanding() kill switch instead.
  const CANVAS_HOME_LAYOUT = "hub";
  const homeCanvasOrdered = useMemo(() => orderCanvases(studioCanvases), [studioCanvases]);
  const requestedCanvasSlug = searchParams.get("cv");
  const activeCanvasSlug = useMemo(() => {
    return (homeCanvasOrdered.find((c) => c.slug === requestedCanvasSlug) || homeCanvasOrdered[0])?.slug || null;
  }, [homeCanvasOrdered, requestedCanvasSlug]);
  // Selecting a canvas exits chat view (clears ?chat) and pins ?cv=.
  const selectCanvasHome = (slug) => {
    mergeUrlParams((next) => {
      if (slug) next.set("cv", slug); else next.delete("cv");
      next.delete("chat");
    }, { push: true });
  };
  // Hub layout: the main column shows the docked chat (not a canvas) when
  // ?chat=1 or on the param-less landing when chat is accessible; a
  // ?cv= deep link still lands on its canvas. chatBar is published by the docked
  // ChatConsole (CanvasHomePage claims the dock) and feeds the sidebar's
  // chat history + New chat.
  const homeChatParam = searchParams.get("chat");

  // ── Fresh-tenant front door ─────────────────────────────────────
  // A tenant with zero endpoints ever reporting gets the Connect surface as
  // the param-less dashboard landing instead of the chat hub — the hub's
  // starter queries have nothing to answer until an agent reports. Fails
  // open (only a CONFIRMED zero engages; 403/error/loading render the
  // landing as today), and the latch holds the surface mounted through the
  // moment the first endpoint checks in — `connected` flips true mid-arc
  // and the live strip must finish its story, not unmount. Leaving the
  // param-less landing (skip sets ?chat=1; a tab click sets ?cv=) releases
  // the latch, and a connected tenant never re-engages it.
  const paramlessHome = currentMode === "dashboard"
    && homeChatParam === null && searchParams.get("cv") === null;
  const connect = useConnectState({
    enabled: dashboardEnabled && currentMode === "dashboard" && canAnalytics,
  });
  const [connectLatch, setConnectLatch] = useState(false);
  useEffect(() => {
    if (paramlessHome && shouldEngageConnectGate(connect)) {
      setConnectLatch(true);
      // Pin the mission (sessionStorage) so a mid-session reload or a
      // one-off fetch failure can't eject the user onto the chat hub —
      // the latch alone is volatile memory and restarts from nothing on
      // every page load. See connectState.js.
      pinConnectFrontDoor();
    } else if (!paramlessHome) {
      setConnectLatch(false);
    }
  }, [paramlessHome, connect]);
  // Only a CONFIRMED-connected tenant releases the pin; unknown, loading,
  // and errored states hold the mission on screen, where the Connect
  // surface's own polling self-heals.
  useEffect(() => {
    if (connect.status === "ready" && connect.connected) unpinConnectFrontDoor();
  }, [connect]);
  const connectGateActive = paramlessHome
    && (connectLatch
      || (isConnectFrontDoorPinned() && !(connect.status === "ready" && connect.connected)));

  const homeSurface = dashboardHomeSurface({ mode: currentMode, chat: homeChatParam, canvas: searchParams.get("cv"), canChat, connectGate: connectGateActive });
  const homeChatActive = CANVAS_HOME_LAYOUT === "hub" && homeSurface === "chat";
  const dockChatBar = useSyncExternalStore(subscribeDock, getDockChatBar, () => null);
  const enterCanvasChat = () => {
    mergeUrlParams((next) => { next.set("chat", "1"); }, { push: true });
  };

  // ── Dashboard home: interleaved open-tab strip ────────────────────
  // One ordered strip across canvases AND chats, in open order. We own the open
  // list; chat entries reference chatBar tab ids (the singleton ChatConsole
  // owns chat lifecycle). The active tab is derived from the URL (?cv= /
  // ?chat=1) + chatBar's active chat — not stored here. Effects keep the
  // list in sync (the shown canvas/chat becomes a tab; chats closed elsewhere
  // drop out); every setHomeTabs guard is idempotent, so no render loop.
  const [homeTabs, setHomeTabs] = useState([]);
  const dashActiveChatId = useMemo(
    () => (dockChatBar?.tabs || []).find((t) => t.active)?.id ?? null,
    [dockChatBar],
  );
  const dashChatTab = (id) => (dockChatBar?.tabs || []).find((t) => t.id === id) || null;
  const isDashTabActive = (kind, id) =>
    kind === "canvas" ? (homeSurface === "canvas" && id === activeCanvasSlug) : (homeChatActive && id === dashActiveChatId);
  const addHomeTab = (kind, id) =>
    setHomeTabs((cur) => (cur.some((t) => t.kind === kind && t.id === id) ? cur : [...cur, { kind, id }]));
  const openDashCanvas = (slug) => { addHomeTab("canvas", slug); selectCanvasHome(slug); };
  const openDashChat = (id) => { addHomeTab("chat", id); dashChatTab(id)?.onSelect?.(); enterCanvasChat(); };
  // Reuse the single empty draft chat if one exists (never stack blank drafts);
  // otherwise create one. A chat only earns a sidebar entry once it's started
  // (gains messages) — see the hasItems filter in CanvasMenu.
  const newDashChat = () => {
    const empty = (dockChatBar?.tabs || []).find((t) => !t.hasItems);
    if (empty) empty.onSelect?.(); else dockChatBar?.onNew?.();
    enterCanvasChat();
  };
  // On the Dashboard, ⌘K opens/focuses the new-chat tab (newDashChat) instead
  // of ChatConsole's default ⌘K — captured so it preempts the global hotkey.
  const newDashChatRef = useRef(null);
  newDashChatRef.current = newDashChat;
  useEffect(() => {
    if (currentMode !== "dashboard" || !canChat) return undefined;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        e.stopImmediatePropagation();
        newDashChatRef.current?.();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [currentMode, canChat]);
  const reorderDashTabs = (from, to) =>
    setHomeTabs((cur) => { const next = cur.slice(); const [moved] = next.splice(from, 1); next.splice(to, 0, moved); return next; });
  const closeDashTab = (kind, id) => {
    const idx = homeTabs.findIndex((t) => t.kind === kind && t.id === id);
    if (idx < 0) return;
    const next = homeTabs.filter((_, i) => i !== idx);
    const wasActive = isDashTabActive(kind, id);
    setHomeTabs(next);
    if (kind === "chat") dashChatTab(id)?.onClose?.();
    if (wasActive) {
      const neighbor = next[idx] || next[idx - 1] || null;
      if (neighbor?.kind === "canvas") selectCanvasHome(neighbor.id);
      else if (neighbor?.kind === "chat") openDashChat(neighbor.id);
      else mergeUrlParams((p) => { p.delete("chat"); }, { push: false });
    }
  };
  useEffect(() => {
    if (homeSurface !== "canvas" || !activeCanvasSlug) return;
    addHomeTab("canvas", activeCanvasSlug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeSurface, activeCanvasSlug]);
  useEffect(() => {
    if (currentMode !== "dashboard" || !homeChatActive || dashActiveChatId == null) return;
    addHomeTab("chat", dashActiveChatId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMode, homeChatActive, dashActiveChatId]);
  // The Dashboard keeps the chat it had in front: the workspace view puts the
  // chat of the workspace being worked in in front of the one console, and
  // coming back here puts this view's own back.
  const dashChatKept = useRef({ mode: currentMode, id: null });
  useEffect(() => {
    const kept = dashChatKept.current;
    const arrived = currentMode === "dashboard" && kept.mode !== "dashboard";
    kept.mode = currentMode;
    if (currentMode !== "dashboard") return;
    const back = arrived && kept.id != null && kept.id !== dashActiveChatId ? dashChatTab(kept.id) : null;
    if (back) {
      back.onSelect?.();
      return;
    }
    if (homeChatActive && dashActiveChatId != null) kept.id = dashActiveChatId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMode, homeChatActive, dashActiveChatId]);
  useEffect(() => {
    if (!dockChatBar) return;
    const live = new Set((dockChatBar.tabs || []).map((t) => t.id));
    setHomeTabs((cur) => {
      const next = cur.filter((t) => t.kind !== "chat" || live.has(t.id));
      return next.length === cur.length ? cur : next;
    });
  }, [dockChatBar]);
  const dashStripTabs = useMemo(() => homeTabs.map((t) => {
    // Like every tab strip, the last remaining tab can't be closed.
    const closable = homeTabs.length > 1;
    if (t.kind === "canvas") {
      const c = homeCanvasOrdered.find((x) => x.slug === t.id);
      return { id: `cv:${t.id}`, label: c?.title || t.id, active: homeSurface === "canvas" && t.id === activeCanvasSlug, onSelect: () => openDashCanvas(t.id), onClose: closable ? () => closeDashTab("canvas", t.id) : undefined };
    }
    const ct = dashChatTab(t.id);
    // Only offer × when ChatConsole can actually close the chat (ct.onClose is
    // undefined on the last chat) — otherwise we'd drop the strip entry while
    // the chat lingers in history.
    return { id: t.id, label: ct?.label || "New chat", dot: ct?.dot, active: homeChatActive && t.id === dashActiveChatId, onSelect: () => openDashChat(t.id), onClose: (closable && ct?.onClose) ? () => closeDashTab("chat", t.id) : undefined };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [homeTabs, homeCanvasOrdered, homeChatActive, homeSurface, activeCanvasSlug, dashActiveChatId, dockChatBar]);
  useEffect(() => subscribeCanvasTemplates(() => {
    const tenantId = studioInventoryTenantIdRef.current;
    setStudioCanvases(AUTH_REQUIRED && !tenantId ? [] : listCanvasTemplates(tenantId));
  }), []);
  // Workflows are IndexedDB-backed → loaded async (vs the synchronous
  // localStorage kernel/canvas lists), refreshed on every store change.
  const [studioWorkflows, setStudioWorkflows] = useState([]);
  useEffect(() => {
    let alive = true;
    const reload = () => {
      const tenantId = studioInventoryTenantIdRef.current;
      if (AUTH_REQUIRED && !tenantId) {
        setStudioWorkflows([]);
        return;
      }
      loadWorkflows(tenantId)
        .then((list) => { if (alive) setStudioWorkflows(list); });
    };
    const unsub = subscribeWorkflows(reload);
    return () => { alive = false; unsub(); };
  }, []);
  // Signals — the native built-in set (read-only; see triggerStore.js).
  const readTriggers = (tenantId) => listTriggers(tenantId);
  const [studioTriggers, setStudioTriggers] = useState(() => (AUTH_REQUIRED ? [] : readTriggers(null)));
  useEffect(() => subscribeTriggers(() => {
    const tenantId = studioInventoryTenantIdRef.current;
    setStudioTriggers(AUTH_REQUIRED && !tenantId ? [] : readTriggers(tenantId));
  }), []);

  // Once the tenant resolves from /v1/me, re-read the studio inventories
  // scoped to it. Legacy `:default` kernels and canvases stay intact until
  // cloud import can attribute and durably map each record.
  useEffect(() => {
    let cancelled = false;
    getCurrentUser()
      .then(async (me) => {
        if (cancelled) return;
        const tenantId = me?.currentTenant?.tenantId ?? null;
        studioInventoryTenantIdRef.current = tenantId;
        setStudioInventoryTenantId(tenantId);
        if (!tenantId) {
          if (AUTH_REQUIRED) {
            setStudioKernels([]);
            setStudioCanvases([]);
            setStudioTriggers([]);
            setStudioWorkflows([]);
            return;
          }
          const workflows = await loadWorkflows(null);
          if (cancelled) return;
          setStudioWorkflows(workflows);
          setStudioInventoryHydrated(true);
          return;
        }
        const userId = me?.userId ?? null;
        studioLegacyImportIdentityRef.current = { tenantId, userId };
        // A cloud Create may have committed even when its response was lost.
        // Resume those durable request ids before the legacy importer sees the
        // same local staging rows (Import has a separate idempotency domain).
        const startup = await initializeTenantViewStudio({ tenantId, userId });
        const workflows = await loadWorkflows(tenantId);
        if (cancelled) return;
        setStudioLegacyImportReport(startup.legacyImport);
        setStudioKernels(listKernels(tenantId));
        setStudioCanvases(listCanvasTemplates(tenantId));
        setStudioTriggers(readTriggers(tenantId));
        setStudioWorkflows(workflows);
        setStudioInventoryHydrated(true);
      })
      .catch(async () => {
        if (cancelled) return;
        studioInventoryTenantIdRef.current = null;
        setStudioInventoryTenantId(AUTH_REQUIRED ? undefined : null);
        setStudioKernels([]);
        setStudioCanvases([]);
        setStudioTriggers([]);
        setStudioWorkflows([]);
        if (AUTH_REQUIRED) return;
        let workflows = [];
        try {
          workflows = await loadWorkflows(null);
        } catch {
          // Inventory readiness cannot depend on IndexedDB availability.
        }
        if (cancelled) return;
        setStudioWorkflows(workflows);
        setStudioInventoryHydrated(true);
      });
    return () => { cancelled = true; };
  }, []);

  const studioInventoriesReady = areStudioInventoriesReady({
    cloudState: studioCloudState,
    hydrated: studioInventoryHydrated,
    tenantId: studioInventoryTenantId,
  });
  const studioSelectedItemExists = !!studioSelectedItem && (
    studioView === "kernels"
      ? studioKernels.some((k) => k.command === studioSelectedItem)
      : studioView === "canvases"
        ? studioCanvases.some((c) => c.slug === studioSelectedItem)
        : studioView === "workflows"
          ? studioWorkflows.some((w) => w.command === studioSelectedItem)
          : studioView === "signals"
            ? studioTriggers.some((t) => t.command === studioSelectedItem)
            : false
  );
  const canvasLinkActive = (currentMode === "dashboard" && homeSurface === "canvas")
    || (currentMode === "explore" && view === "scope.canvases");
  const unavailableStudioLink = isUnavailableStudioLink({
    activeTab: studioActiveTab,
    canvases: studioCanvases,
    currentMode,
    inventoriesReady: studioInventoriesReady,
    kernels: studioKernels,
    triggers: studioTriggers,
    workflows: studioWorkflows,
  });
  const unavailableCanvasLink = isUnavailableCanvasLink({
    active: canvasLinkActive,
    canvases: homeCanvasOrdered,
    hasSelection: searchParams.has("cv"),
    inventoriesReady: studioInventoriesReady,
    slug: requestedCanvasSlug,
  });
  const unavailableExplorerLink = isUnavailableExplorerLink({
    activeTab: explorerActiveTab,
    currentMode,
    influenceTab,
    scopePath,
  });

  // Reap stale studio tabs. When a kernel or canvas is deleted (via
  // the detail page, chat tools, or any other path), any open tab
  // that still points at it becomes a zombie — clicking it lands on
  // the StudioOverview fallback, and the tab itself never goes away.
  // Watch the live inventories and prune tabs that no longer have a
  // matching item, adjusting activeIdx so the user's focus stays
  // anchored to a real tab.
  useEffect(() => {
    if (unavailableStudioLink) return;
    const next = nextStudioTabState({
      activeIdx: studioActiveIdx,
      canvases: studioCanvases,
      inventoriesReady: studioInventoriesReady,
      kernels: studioKernels,
      tabs: studioTabs,
      triggers: studioTriggers,
      workflows: studioWorkflows,
    });
    if (!next) return;
    if (next.tabs.length === 0) {
      writeStrip(STUDIO_STRIP, [STUDIO_STRIP.defaultTab], 0);
    } else {
      writeStrip(STUDIO_STRIP, next.tabs, next.activeIdx);
    }
    // STUDIO_STRIP / writeStrip close over current url state; depending
    // only on the inputs that should *trigger* a reap keeps this from
    // looping on its own writes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studioKernels, studioCanvases, studioWorkflows, studioTriggers, studioInventoriesReady, unavailableStudioLink]);

  // A detail page is only *actually* rendering when the URL points at
  // a view + item AND that item still exists. A stale URL pointing at
  // a deleted (or never-existed) kernel/canvas falls back to
  // StudioOverview, in which case we still want the inline tab strip
  // above the page rather than baked into a non-existent SplitPage.
  const detailRendering = studioSelectedItemExists;
  const studioPageOwnsHeader = detailRendering || studioView === "skills";

  // ── Sidebar toggle + resize ──

  const currentSidebarWidth = currentMode === "security" || sidebarCollapsed
    ? COLLAPSED_SIDEBAR_WIDTH
    : clampSidebarWidth(sidebarWidth);

  const SIDEBAR_TABLE_MIN = 400;
  const showTableView = currentSidebarWidth >= SIDEBAR_TABLE_MIN && !sidebarCollapsed;

  // Ensure columns are ready when table mode is selected
  if (showTableView && !columnsReady) setColumnsReady(true);

  function handleSidebarToggle() {
    const sidebar = sidebarRef.current;
    const shell = shellRef.current;
    if (!sidebar || !shell) {
      setSidebarCollapsed((v) => !v);
      return;
    }

    const collapsing = !sidebarCollapsed;
    const ease = "cubic-bezier(0.25, 0.1, 0.25, 1)";

    const mainEl = shell.querySelector("[data-main]");
    const onTransitionEnd = (e) => {
      if (e.propertyName !== "left") return;
      mainEl?.removeEventListener("transitionend", onTransitionEnd);
      shell.classList.remove("is-animating-sidebar");
      sidebar.style.cssText = "";
    };

    if (collapsing) {
      sidebar.style.transition = `transform 350ms ${ease}, opacity 150ms ease-out`;
      sidebar.style.transform = "translateX(-100%)";
      sidebar.style.opacity = "0";
      shell.classList.add("is-animating-sidebar");
      void mainEl?.offsetWidth;
      setTimeout(() => setSidebarCollapsed(true), 100);
      mainEl?.addEventListener("transitionend", onTransitionEnd);
    } else {
      sidebar.style.transform = "translateX(-100%)";
      sidebar.style.opacity = "0";
      sidebar.style.transition = "none";
      shell.classList.add("is-animating-sidebar");
      void mainEl?.offsetWidth;
      setSidebarCollapsed(false);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          sidebar.style.transition = `transform 350ms ${ease}, opacity 200ms ease-in 80ms`;
          sidebar.style.transform = "translateX(0)";
          sidebar.style.opacity = "1";
        });
      });
      mainEl?.addEventListener("transitionend", onTransitionEnd);
    }
  }

  function handleResizerPointerDown(event) {
    if (sidebarCollapsed) return;
    event.preventDefault();

    const startX = event.clientX;
    const startWidth = sidebarRef.current?.getBoundingClientRect().width || sidebarWidth;
    document.body.classList.add("is-resizing-sidebar");

    const unlockFns = [];
    const main = shellRef.current?.querySelector("[data-main]");
    if (main) {
      for (const attr of [
        "data-endpoint-content-scroll",
        "data-content-scroll",
        "data-context-scroll",
        "data-main-scroll",
      ]) {
        const host = main.querySelector(`[${attr}]`);
        if (!host) continue;
        const viewport =
          host.querySelector("[data-overlayscrollbars-viewport]") ||
          host.querySelector(".os-viewport");
        const content = host.querySelector("[data-scroll-content]");
        if (content) {
          const w = `${content.offsetWidth}px`;
          content.style.minWidth = w;
          unlockFns.push(() => {
            content.style.minWidth = "";
          });
        }
        if (viewport) {
          viewport.style.overflowX = "hidden";
          unlockFns.push(() => {
            viewport.style.overflowX = "";
          });
        }
      }
    }

    let rafId = 0;
    let latestX = startX;

    const onMove = (moveEvent) => {
      latestX = moveEvent.clientX;
      if (!rafId) {
        rafId = requestAnimationFrame(() => {
          rafId = 0;
          setSidebarWidth(clampSidebarWidth(startWidth + latestX - startX));
        });
      }
    };

    const onUp = () => {
      if (rafId) cancelAnimationFrame(rafId);
      for (const fn of unlockFns) fn();
      document.body.classList.remove("is-resizing-sidebar");
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }

  // ── Render ──

  if (!workerReady) return <StartupScreen error={startupError} />;

  // Per-mode tab strips. Explore renders the scope strip; Library renders
  // the studio strip; Endpoints/Admin select one child view. Each mode's
  // state is preserved across mode
  // switches so the user can flip between Explore and Studio and
  // come back to their tabs intact.
  const explorerHeader = (
    <FocusTabStrip
      dimmed={inPinMode}
      tabs={explorerTabs}
      activeIdx={explorerActiveIdx}
      onActivate={(i) => activateTab(EXPLORER_STRIP, i)}
      onClose={(i) => closeTab(EXPLORER_STRIP, i)}
      onOpen={() => openTab(makeScopeTab([]), { forceNew: true })}
      onReorder={(f, t) => reorderTab(EXPLORER_STRIP, f, t)}
      onJumpStep={jumpStep}
      onOpenStepInNew={openStepInNew}
      onDropStep={dropStep}
    />
  );
  // "+" in the Studio tab strip opens a menu to create a new library
  // item — kernel, canvas, or workflow — and opens it in a fresh tab.
  // Other Studio tabs (overview, existing items) come from sidebar
  // clicks; this is the only path that adds a NEW item to the library.
  const createLibraryItem = async (kind) => {
    try {
      if (kind === "kernels") {
        const tenantId = studioInventoryTenantIdRef.current;
        if (AUTH_REQUIRED && !tenantId) return;
        selectStudioItem("kernels", (await createBlankKernelCloudFirst(tenantId)).command);
      } else if (kind === "canvases") {
        const tenantId = studioInventoryTenantIdRef.current;
        if (AUTH_REQUIRED && !tenantId) return;
        selectStudioItem("canvases", (await createBlankCanvasTemplateCloudFirst(tenantId)).slug);
      }
      else if (kind === "workflows") createWorkflow();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`[studio] create ${kind} failed:`, err);
    }
  };
  const approveLegacyImportSecrets = async (approvalKey, secretOverrides) => {
    const identity = studioLegacyImportIdentityRef.current;
    if (!identity?.tenantId || !identity?.userId) {
      throw new Error("Studio migration identity is unavailable");
    }
    const startup = await initializeTenantViewStudio({
      ...identity,
      secretOverrideApprovals: { [approvalKey]: secretOverrides },
    });
    setStudioLegacyImportReport(startup.legacyImport);
    setStudioKernels(listKernels(identity.tenantId));
    setStudioCanvases(listCanvasTemplates(identity.tenantId));
    setStudioTriggers(readTriggers(identity.tenantId));
  };
  const studioHeader = (
    <StudioFocusHeader
      strip={{
        tabs: studioTabs,
        activeIdx: studioActiveIdx,
        onActivate: (i) => activateTab(STUDIO_STRIP, i),
        onClose: (i) => closeTab(STUDIO_STRIP, i),
        newTabMenu: [
          { label: "New kernel", onSelect: () => createLibraryItem("kernels") },
          { label: "New canvas", onSelect: () => createLibraryItem("canvases") },
          { label: "New workflow", onSelect: () => createLibraryItem("workflows") },
        ],
        onReorder: (f, t) => reorderTab(STUDIO_STRIP, f, t),
        onJumpStep: () => {},
        onOpenStepInNew: () => {},
        onDropStep: () => {},
      }}
      legacyImportReport={studioLegacyImportReport}
      onApproveLegacySecrets={approveLegacyImportSecrets}
      onDismissLegacyReport={() => setStudioLegacyImportReport(null)}
    />
  );
  // ScopedPage always gets the explorer header (it's the explore surface).
  // The admin overlay branches by mode. In pin mode the strip stays mounted
  // but the full-screen surface expands OVER it (cover-header, see
  // commandFocusColumn → ContentShell), so it's covered + non-interactive
  // with no layout shift — and revealed again as the surface collapses.
  const focusHeader = explorerHeader;

  if (unavailableStudioLink || unavailableCanvasLink || unavailableExplorerLink) {
    return <UnavailableLinkPage onOpenDashboard={openCurrentOrganizationHome} />;
  }
  return (
    <div
      ref={shellRef}
      className="page-enter"
      data-shell
      data-sidebar-collapsed={currentMode === "security" || sidebarCollapsed ? "true" : "false"}
      style={{ "--sidebar": `${currentSidebarWidth}px` }}
    >
      <aside data-origin-rail>
        {/* Top-of-rail interactive stack: drawer chevron (only visible
            when the sidebar is collapsed) + navigation buttons. All
            share the same boxed treatment so they read as a single
            family. The chevron is only RENDERED — not just opacity:0
            — when the sidebar is open, so there's no empty reserved
            slot above the modes. Modes shift up/down by one button's
            height when the drawer toggles, in sync with the
            sidebar's slide animation. */}
        <nav data-origin-modes aria-label="Main navigation">
          {/* Drawer chevron — always rendered, but CSS collapses its
              box to height 0 + opacity 0 when the sidebar is open,
              animating in/out of the stack. Mode buttons slide
              smoothly into place rather than jump. */}
          {currentMode !== "security" && <HoverTooltip label="Expand drawer">
            <button
              data-origin-mode="drawer"
              data-visible={sidebarCollapsed ? "true" : "false"}
              type="button"
              aria-label={currentMode === "security" && adminView === "alerts" ? "Open security navigation" : "Expand drawer"}
              aria-hidden={!sidebarCollapsed}
              tabIndex={sidebarCollapsed ? 0 : -1}
              onClick={handleSidebarToggle}
            >
              <SidebarToggleIcon collapsed />
            </button>
          </HoverTooltip>}
          {MODES.map((m) => {
            // Hide the Explore rail entry from a role without analytics
            // access. Fails open while caps load (see
            // canAnalytics), so the entry never flash-hides.
            // Dashboard (the default landing) runs analytics kernels — hide it
            // from a role without analytics, same as Explore; also hidden when
            // the front door is reverted to Explore (dashboardEnabled).
            if (m.id === "dashboard" && (!canAnalytics || !dashboardEnabled)) return null;
            if (m.id === "explore" && !canAnalytics) return null;
            // Security combines review findings and signal alerts. Its view-level
            // permission gate decides visibility; automatic review being disabled
            // must not hide signal alerts that already exist.
            if (m.id === "security" && !firstVisibleViewForMode("security", permissionPosture, entitlements)) return null;
            // Library remains available to a skills-only role when that
            // feature is enabled, even if analytics authoring is unavailable.
            if (m.id === "studio" && !canAnalytics && !ADMIN_VIEW_IDS.has("skills")) return null;
            if (m.id === "endpoints" && !firstVisibleViewForMode("endpoints", permissionPosture, entitlements)) return null;
            if (m.id === "governance" && !firstVisibleViewForMode("governance", permissionPosture, entitlements)) return null;
            if (m.id === "admin" && !firstVisibleViewForMode("admin", permissionPosture, entitlements)) return null;
            // While pivoted to the overview home, the home ⌂ is the active
            // rail item — so Explore yields its highlight, making "click
            // Explore to get back" read correctly (no two-active conflict).
            const active = m.id === currentMode && adminView !== "report" && !(m.id === "explore" && inPinMode);
            const Icon = MODE_ICONS[m.id];
            // In the workspace already, its button goes back to the overview
            // in the pane worked in; the workspace that showed stays in the
            // sidebar's list.
            const home = m.id === "workspace" && currentMode === "workspace";
            return (
              <Fragment key={m.id}>
              <HoverTooltip label={home && !overviewInFront ? `${m.label}: back to the overview` : `${m.label}: ${m.note}`}>
                <button
                  type="button"
                  data-origin-mode={m.id}
                  data-active={active ? "true" : "false"}
                  aria-current={active ? "page" : undefined}
                  aria-label={m.label}
                  {...phAttrs({ [ATTRIBUTE.NAV_MODE]: m.id })}
                  onClick={() => (home ? startWorkspaceInDeck() : selectMode(m.id))}
                >
                  {Icon ? <Icon /> : <span aria-hidden="true">[{m.rail}]</span>}
                </button>
              </HoverTooltip>
              {/* Overview pins — one pushpin per pinned scope, between Explore
                  and Library. Each reuses the [data-origin-mode] height-collapse
                  (data-visible) so they slide in/out with Explore like the
                  drawer chevron. Click flips to that pin full-screen (active
                  while it's the one you're viewing); tooltip names the scope. */}
              {m.id === "explore"
                ? pins.map((pin) => {
                    const active = inPinMode && activePinScope === pin.scope;
                    const label = pin.scope
                      ? (decodeScopePath(pin.scope) || []).map(scopeStepLabel).join(" / ")
                      : "Overview";
                    return (
                      <HoverTooltip
                        key={pin.scope}
                        label={active ? `${label}. Exit full screen.` : `${label}. Open full screen.`}
                      >
                        <button
                          type="button"
                          data-origin-mode="overview"
                          data-visible={currentMode === "explore" ? "true" : "false"}
                          data-active={active ? "true" : "false"}
                          aria-hidden={currentMode !== "explore"}
                          tabIndex={currentMode === "explore" ? 0 : -1}
                          aria-pressed={active}
                          aria-label={`Pinned scope: ${label}`}
                          onClick={() => togglePin(pin)}
                        >
                          <svg viewBox="-4 -4 32 32" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 17v5" />
                            <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z" />
                          </svg>
                        </button>
                      </HoverTooltip>
                    );
                  })
                : null}
              </Fragment>
            );
          })}
          <HoverTooltip label="Send feedback or report a bug">
            <button
              type="button"
              data-origin-mode="report"
              data-active={adminView === "report" ? "true" : "false"}
              aria-current={adminView === "report" ? "page" : undefined}
              aria-label="Send feedback or report a bug"
              onClick={() => selectAdminView("report")}
            >
              <svg viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 6.5h6v4a3 3 0 0 1-6 0z" />
                <path d="M6 6.5V5a2 2 0 0 1 4 0v1.5M6.5 3.5 5 2M9.5 3.5 11 2M8 6.5v7" />
                <path d="m2 5 3 2m6 0 3-2M2 9h3m6 0h3m-12 4 3-2m6 0 3 2" />
              </svg>
            </button>
          </HoverTooltip>
        </nav>
        <div className="self-end w-[16px] overflow-visible py-[5px]">
          <HoverTooltip label="Return to overview · reset panel layout">
            <button
              type="button"
              className="block w-full cursor-pointer border-0 bg-transparent p-0 text-[color:var(--rail-ink)]"
              aria-label="Return to overview and reset panel layout"
              onClick={() => { updateScopePath([]); resetPanelLayout(); }}
            >
              <OriginMark className="w-full" />
            </button>
          </HoverTooltip>
        </div>
      </aside>

      <aside ref={sidebarRef} data-sidebar data-menu-open={menuOpen ? "true" : "false"}>
        {/* Explore-only topbar — date range + generation pin + collapse.
            Library, Endpoints, and Admin render their own headers inside their
            menus, so the topbar is gated on mode rather than animated
            in/out. GenerationPicker self-gates (admins / local dev under
            admin/local-dev access, or an active pin) and renders null otherwise. */}
        {currentMode === "explore" || currentMode === "dashboard" || currentMode === "workspace" ? (
          <div
            data-sidebar-topbar
            className="relative flex items-end gap-[2px] pt-[11px] border-b border-[color:var(--line)] overflow-hidden"
          >
            <DateRangePicker range={range} onRangeChange={updateRange} />
            {/* Clustering/generation selector is Explore-only — too much for
                the Canvases home (the "for dummies" surface). */}
            {currentMode === "explore" ? <GenerationPicker /> : null}
            <button
              data-sidebar-toggle
              className="grid h-[38px] w-[34px] shrink-0 place-items-center border-x border-t border-[color:var(--line)] bg-transparent p-0 text-[color:var(--muted)] [&_svg]:block [&_svg]:h-3 [&_svg]:w-3"
              type="button"
              aria-label={sidebarCollapsed ? "Expand drawer" : "Collapse drawer"}
              onClick={handleSidebarToggle}
            >
              <SidebarToggleIcon collapsed={sidebarCollapsed} />
            </button>
          </div>
        ) : null}

        {/* Sidebar body — content swaps based on rail mode. No slide
            transitions; the rail buttons make the mode switch the
            navigation event, so the body just changes content
            accordingly. The scope-columns table-view overlay only
            applies in Explore mode. */}
        <div className="relative min-h-0 min-w-0 h-full overflow-hidden">
          {currentMode === "dashboard" ? (
            <CanvasMenu
              canvases={studioCanvases}
              selectedSlug={homeSurface === "canvas" ? activeCanvasSlug : null}
              onSelect={openDashCanvas}
              layout={CANVAS_HOME_LAYOUT}
              chatEnabled={canChat}
              chatBar={dockChatBar}
              chatActive={homeChatActive}
              onOpenChat={openDashChat}
              onNewChat={newDashChat}
            />
          ) : currentMode === "explore" ? (
            <>
              <div
                className="absolute inset-0 h-full"
                style={{
                  opacity: showTableView ? 0 : 1,
                  pointerEvents: showTableView ? "none" : undefined,
                }}
              >
                <ScrollArea data-sidebar-scroll className="min-h-0 h-full">
                  <ScopeSidebar scopePath={sidebarScopePath} onChange={onSidebarChange} onFocusDim={navigateFocusDim} focusDim={focusDim} onView={navigateView} view={view} cacheRef={cacheRef} steers={steers} setSteer={setSteer} makeSteerSuggest={makeSteerSuggest} />
                </ScrollArea>
              </div>
              {columnsReady && (
                <div
                  className="absolute inset-0 h-full border-x border-[color:var(--line)] bg-[color:var(--panel-soft)] transition-opacity duration-200"
                  style={{
                    opacity: showTableView ? 1 : 0,
                    pointerEvents: showTableView ? undefined : "none",
                    contain: "strict",
                  }}
                >
                  <ScopeColumns
                    scopePath={sidebarScopePath}
                    onChange={onSidebarChange}
                    visible={showTableView}
                    flowHaloColor="var(--panel-soft)"
                    cacheRef={cacheRef}
                    cacheVersion={cacheTick}
                  />
                </div>
              )}
            </>
          ) : currentMode === "workspace" ? (
            // The workspace's sessions, kept as a chat harness keeps threads.
            <WorkspaceThreads range={range} />
          ) : currentMode === "security" ? null : currentMode === "studio" ? (
            <StudioMenu
              kernels={studioKernels}
              canvases={studioCanvases}
              workflows={studioWorkflows}
              selectedView={["kernels", "canvases", "workflows", "skills"].includes(adminView) ? adminView : null}
              selectedItemId={studioSelectedItem}
              onSelectItem={selectStudioItem}
              onCollapseDrawer={sidebarCollapsed ? null : handleSidebarToggle}
              skillsVisible={ADMIN_VIEW_IDS.has("skills")}
            />
          ) : (
            <AdminMenu
              activeView={adminView}
              entitlements={entitlements}
              mode={currentMode}
              onSelect={selectAdminView}
              onCollapseDrawer={sidebarCollapsed ? null : handleSidebarToggle}
            />
          )}
        </div>

        <SidebarFooter
          theme={theme}
          onToggleTheme={() => setTheme((v) => (v === "dark" ? "light" : "dark"))}
          onOpenAccount={() => selectAdminView("account")}
          onSendFeedback={() => selectAdminView("report")}
        />

        <button
          data-sidebar-resizer
          type="button"
          aria-label="Resize drawer"
          onPointerDown={handleResizerPointerDown}
          onDoubleClick={() => setSidebarWidth(DEFAULT_SIDEBAR_WIDTH)}
        />
      </aside>

      {/* ScopedPage stays mounted while the admin menu is open so we
          don't pay a full remount when toggling back (data fetches,
          chart re-render, virtualizer re-measurement). AdminPage is
          layered above as an absolute overlay — [data-main] is already
          position:absolute, so it's the containing block for the
          overlay. The wrapper div around ScopedPage is the content row in
          [data-main]; the optional connector-health banner occupies the row
          above it.

          adminOverlayActive is true whenever the main canvas should
          show an admin/studio surface instead of the scope explorer.
          That includes the "Studio library overview" landing where
          mode=studio but no specific view is picked yet. */}
      {(() => {
        const adminOverlayActive = adminView != null || currentMode === "studio" || currentMode === "dashboard" || currentMode === "workspace";
        return (
          <div
            data-main
            data-layout={adminOverlayActive ? "admin" : "endpoint"}
            style={{ gridTemplateRows: "auto minmax(0, 1fr)" }}
          >
            <ComplianceIntegrationHealthBanner onOpenDataSources={() => selectAdminView("data-sources")} />
            <div className="relative row-start-2 h-full min-h-0 min-w-0 overflow-hidden">
              <div
                style={{
                  height: "100%",
                  minHeight: 0,
                  minWidth: 0,
                  visibility: adminOverlayActive ? "hidden" : "visible",
                }}
                inert={adminOverlayActive || undefined}
                aria-hidden={adminOverlayActive ? "true" : undefined}
              >
                <ScopedPage
                key={`scoped-${layoutNonce}`}
                scopePath={scopePath}
                influenceTab={influenceTab}
                onOpenInfluenceTab={openInfluenceTab}
                onOpenScopeTab={(path) => openTab(makeScopeTab(path || []))}
                range={range}
                activityLens={coerceComparisonLens(
                  // A pinned home can carry lens:"clusters"; coerce when the gate is off.
                  surfaceScopeOverride ? (getPin(activePinScope)?.lens || activityLens) : activityLens,
                  comparisonsEnabled(),
                )}
                focusDim={focusDim}
                view={view}
                surfaceScopeOverride={surfaceScopeOverride}
                endpointTab={endpointTab}
                logTab={logTab}
                theme={theme}
                focusHeader={focusHeader}
                onRangeChange={updateRange}
                onActivityLensChange={surfaceScopeOverride ? setHomeLens : setActivityLens}
                onEndpointTabChange={setEndpointTab}
                onLogTabChange={setLogTab}
                onScopePathChange={updateScopePath}
                dockVisible={!adminOverlayActive && !canvasUp}
                steerQuery={steerApplied}
                onSteerScope={(text) => setSteer(encodeScopePath(scopePath), text)}
                />
              </div>
              {adminOverlayActive ? (
                <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "grid",
                  // Reserve a top row for the inline tab strip whenever
                  // we're not actively rendering a detail page that
                  // bakes the strip into its own chrome. The
                  // `detailRendering` check confirms not just that
                  // the URL claims a view + item, but that the item
                  // actually exists in storage — a stale URL pointing
                  // at a deleted kernel/canvas falls back to
                  // StudioOverview, which doesn't accept focusHeader.
                  gridTemplateRows: ((currentMode === "studio" && !studioPageOwnsHeader) || currentMode === "dashboard")
                    ? "auto minmax(0,1fr)"
                    : "minmax(0,1fr)",
                  gridTemplateColumns: "minmax(0,1fr)",
                  background: "var(--bg)",
                  // Above SplitPage's detail column (zIndex:10) so the
                  // right Context panel — which now stays mounted across
                  // collapse toggles — doesn't bleed through when admin is
                  // up.
                  zIndex: 20,
                  overflow: "hidden",
                }}
              >
                {currentMode === "studio" && !studioPageOwnsHeader ? (
                  <div className="min-w-0 overflow-hidden px-[var(--main-inset)]">
                    {studioHeader}
                  </div>
                ) : currentMode === "dashboard" ? (
                  // Interleaved open-tab strip across canvases AND chats (open
                  // order, × to close, + opens a new chat). The active tab —
                  // canvas or chat — drives the main column below.
                  <div className="min-w-0 overflow-hidden px-[var(--main-inset)]">
                    <TabStrip
                      className="pt-[11px] max-[900px]:pt-[8px]"
                      tabs={dashStripTabs}
                      onReorder={reorderDashTabs}
                      actions={CANVAS_HOME_LAYOUT === "hub" && canChat ? (
                        <button
                          type="button"
                          onClick={newDashChat}
                          {...phAttrs({ [ATTRIBUTE.ACTION]: ACTION.NEW_CHAT })}
                          aria-label="New chat"
                          title="New chat"
                          className="flex min-h-[38px] w-[34px] shrink-0 items-center justify-center border border-b-0 border-[color:var(--line)] bg-[color:var(--bg)] [background-image:linear-gradient(var(--control-bg),var(--control-bg))] text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)] [&_svg]:block"
                        >
                          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" className="h-[12px] w-[12px]"><path d="M8 3.5v9M3.5 8h9" /></svg>
                        </button>
                      ) : null}
                    />
                  </div>
                ) : null}
                {adminView ? (
                  <AdminPage
                    entitlements={entitlements}
                    view={adminView}
                    selectedItem={studioSelectedItem}
                    onSelectItem={selectStudioItem}
                    onRenameItem={renameStudioItem}
                    onCloseItem={closeStudioItem}
                    onScopeNavigate={openInExplore}
                    focusHeader={currentMode === "studio" && detailRendering ? studioHeader : null}
                  />
                ) : currentMode === "workspace" ? (
                  // The open workspace: its board and its dock. + Add finds
                  // in the sidebar's sessions, opening the drawer if it is
                  // shut.
                  <section className="flex h-full min-h-0 min-w-0 flex-col bg-[color:var(--bg)]">
                    <WorkspaceHome
                      range={range}
                      onRangeChange={updateRange}
                      onFind={() => {
                        if (sidebarCollapsed) handleSidebarToggle();
                        setTimeout(requestThreadsSearch, 0);
                      }}
                    />
                  </section>
                ) : currentMode === "dashboard" ? (
                  // Dashboard landing and explicit canvas/chat views. Same chrome as
                  // a routed admin view; the selected canvas renders tenant-wide.
                  // The Connect mission takes the front door instead while the
                  // tenant has no endpoints (see connectGateActive above).
                  <section className="flex h-full min-h-0 min-w-0 flex-col bg-[color:var(--bg)] px-[var(--main-inset)]">
                    {connectGateActive ? (
                      <ConnectPage
                        embedded
                        onSkip={canChat ? enterCanvasChat : () => selectCanvasHome(activeCanvasSlug)}
                        onScopeNavigate={openInExplore}
                      />
                    ) : (
                      <CanvasHomePage
                        canvases={studioCanvases}
                        selectedSlug={activeCanvasSlug}
                        range={range}
                        onOpenInExplore={openInExplore}
                        layout={CANVAS_HOME_LAYOUT}
                        chatActive={homeChatActive}
                      />
                    )}
                  </section>
                ) : (
                  // Studio library landing — mode=studio but no specific
                  // view picked yet. AdminPage's section wrapper is
                  // recreated inline so the overview gets the same
                  // background + main-inset chrome as a routed admin view.
                  <section className="flex h-full min-h-0 min-w-0 flex-col bg-[color:var(--bg)] px-[var(--main-inset)]">
                    <StudioOverview
                      kernels={studioKernels}
                      canvases={studioCanvases}
                      workflows={studioWorkflows}
                      onOpenKernel={(cmd) => selectStudioItem("kernels", cmd)}
                      onOpenCanvas={(slug) => selectStudioItem("canvases", slug)}
                      onOpenWorkflow={(cmd) => selectStudioItem("workflows", cmd)}
                    />
                  </section>
                )}
                </div>
              ) : null}
            </div>
          </div>
        );
      })()}
      {/* The one chat instance. Unmounted (not just hidden) when the caller
          can't read the LLM provider descriptor — this also disarms its
          global ⌘K hotkey and its dock/chatBar publishing. */}
      {canChat ? <ChatConsole key={authContextEpoch} onOpenStudioItem={openStudioItemExternal} /> : null}
      {/* On the home the open workspace is the page itself. */}
      {currentMode === "workspace" ? null : <TraceCanvasOverlay range={range} onRangeChange={updateRange} />}
    </div>
  );
}

// Auth changes remount every mounted tenant-derived surface at once. This is
// the boundary for chat histories/runs and Studio inventories whose component
// state is intentionally long-lived within one authenticated session.
export default function App() {
  const authIdentityEpoch = useSyncExternalStore(onAuthIdentityChange, getAuthIdentityEpoch, getAuthIdentityEpoch);
  if (isSigningOut()) return null;
  return (
    <AuthIdentityGate identityEpoch={authIdentityEpoch}>
      <TenantLinkGate>
        <ComplianceIntegrationHealthProvider key={authIdentityEpoch}>
          <AppContent authContextEpoch={authIdentityEpoch} />
        </ComplianceIntegrationHealthProvider>
      </TenantLinkGate>
    </AuthIdentityGate>
  );
}
